/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  call,
  cancelled,
  delay,
  select,
  take,
  takeLatest,
  put,
  fork,
  spawn,
  race,
} from 'redux-saga/effects';
import { produce } from 'immer';
import * as api from '@api/readerMeasurements';
import * as screenshotApi from '@api/uploadScreenshot';
import {
  actions,
  isMeasurementWriteSettled,
  measurementsSlice,
  selectMeasurementsState,
} from './measurements.slice';
import {
  resolveMeasurementContract,
  round2,
  toMeasuredValue,
  naMeasuredValue,
  valueFromCachedStats,
} from './measurementContracts';
import {
  AppendRetryEntry,
  EnqueueAppendAnnotationPayload,
  EnqueueRemoveAnnotationPayload,
  EnqueueUpdateMeasurementPayload,
  MeasurementsCreateAction,
  MeasurementsDeleteAction,
  MeasurementsHandleToolAction,
  MeasurementsState,
  MeasurementsUpdateAction,
} from './measurements.types';
import {
  AnnotationInGroup,
  AnnotationInstanceObjectType,
  Measurement,
  MeasurementConfig,
  MeasurementDataPayload,
  MeasurementEventData,
  MeasurementItem,
  MeasurementReqParams,
  MeasurementsScreenshotStoreAction,
  MeasurementsStoreAction,
  MeasurementsStoreActionType,
  SchemaAnnotation,
  EntryKey,
} from '@dynamika-shared/types/measurements';
import {
  LesionToolNamesEnum,
  resolveToolName,
} from '@dynamika-shared/types/customInstruments';
import { ACTIVATE_DEACTIVATE_TIMEOUT_MS } from '@shared/constants/measurementActivation';
import { getSchema } from './schemas';
import type { ExtractValuesContext } from './schemas/types';
import { dispatch, Resp } from '../helpers';
import { actions as formManagementActions } from '../readerViewerSession/formManagement/formManagement.slice';
import { ViewportSeriesData } from '@dynamika-shared/services/communication';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  selectDataRelatedToMeasurements,
  selectMeasurementByCode,
  selectMeasurementsByIds,
  selectMeasurementScreenshot,
  selectStoreDataRelatedToMeasurements,
} from './measurements.selectors';
import {
  scoringFormMeasurementsChangeAction,
  scoringFormSeriesChangedAction,
} from 'src/pages/Readings/ReadingTaskPage/utils';
import { Gen } from '@dynamika-shared/types/general';
import _ from 'lodash';
import { ReaderTaskVisits } from '@dynamika-shared/types/reader';
import { UserRoles } from '@dynamika-shared/types/auth';
import { runToast, showErrorToast } from '../tools';
import {
  createKeepWatcherAlive,
  WatcherWorker,
} from '../keepWatcherAlive';
import { publishEditEnd, publishEditStart } from '@utils/measurementChannel';
import { authSlice } from '../auth/auth.slice';
import { clinicalTrialInventorySlice } from '../clinicalTrialInventory/clinicalTrialInventory.slice';
import { Rpc } from '@dynamika/rpc-core/contracts';
import { viewerRpcScheme as viewerRpc } from 'src/services/rpc/viewer/scheme';
import { selectTabCoordinationState } from '@restore/readerViewerSession/utils';
import {
  resolveFormCall,
  rejectFormCall,
} from 'src/services/rpc/pendingCalls';
import {
  isPeerDestroyedError,
  isRpcTimeoutError,
} from 'src/services/rpc/helpers';
import { isViewerGone } from 'src/services/rpc/viewerPresence';
import { takeQueue } from '@dynamika-shared/utils/saga';

// ---------------------------------------------------------------------------
// The cachedStats → BE "Value" helpers and the per-scoring-form measurement
// contracts now live in `measurementContracts.ts`. Re-exported here so existing
// importers and tests keep their `./measurements.saga` import path.
// ---------------------------------------------------------------------------
export { round2, toMeasuredValue, naMeasuredValue, valueFromCachedStats };

const base64ToBlob = (image64: string): Promise<Blob> =>
  fetch(image64).then(res => res.blob());

export const SCREENSHOT_UPLOAD_TIMEOUT_ERROR = new Error(
  'Screenshot upload timed out'
);

export function findScoringCode(visits: ReaderTaskVisits[]) {
  return visits?.find(visit => !!visit.scoringCode)?.scoringCode;
}

const resolveIsMeasurable = (fromRecord?: boolean, fromForm?: boolean): boolean =>
  fromRecord ?? fromForm ?? true;

/**
 * Resolve the stored measurement for a tool payload. Prefer the record id the
 * form sends (segment.measurementInstanceId) — it identifies the measurement
 * unambiguously. Fall back to a by-code lookup only when the form has no id yet
 * (a brand-new measurement). A by-code lookup alone is unsafe when versioned
 * forms share segment codes (LIVER_PDFF vs LIVER_PDFF_V2): the first inventory
 * match can be the other version, so the stored measurement is not found.
 */
export function* selectMeasurementForPayload(
  payload: MeasurementsHandleToolAction['payload']
) {
  const byId: ReturnType<typeof selectMeasurementsByIds> = yield select(
    selectMeasurementsByIds,
    payload.measurementId ? [payload.measurementId] : []
  );
  if (byId[0]) {
    return byId[0];
  }
  const byCode: ReturnType<typeof selectMeasurementByCode> = yield select(
    selectMeasurementByCode,
    payload.measurementConfig?.code ?? '',
    payload.index ?? 0
  );
  return byCode;
}

/**
 * Stamp the form-supplied `format` onto `resolvedConfig.measurementConfig.metadata`
 * before it crosses into the viewer, so the RPC payload carries a real value
 * while the BE inventory's `metadata` stays empty.
 *
 * Plain terms
 * -----------
 * The backend measurement list doesn't yet say how to display a lesion's
 * LAD/SAD numbers (decimals, rounding). Until it does, the RANO scoring form
 * supplies it per drawing tool, alongside the activation payload.
 *
 * Technical
 * ---------
 * `resolvedConfig.format` is a top-level field the form attaches to the
 * activation payload — sent alongside, not nested inside, `measurementConfig`
 * so it survives the re-anchoring swap above, which replaces
 * `measurementConfig` wholesale on grouped tasks. Applied to the FINAL
 * `resolvedConfig`, after that swap. A BE-supplied `metadata.format` always
 * wins over the form's stopgap value.
 */
export const applyFormatOverride = (
  resolvedConfig: MeasurementsHandleToolAction['payload']
): MeasurementsHandleToolAction['payload'] => {
  const existingFormat = resolvedConfig.measurementConfig?.metadata?.format;
  const formFormat = resolvedConfig.format;

  if (existingFormat || !formFormat || !resolvedConfig.measurementConfig) {
    return resolvedConfig;
  }

  return {
    ...resolvedConfig,
    measurementConfig: {
      ...resolvedConfig.measurementConfig,
      metadata: {
        ...resolvedConfig.measurementConfig.metadata,
        format: formFormat,
      },
    },
  };
};

/**
 * Arms or disarms a lesion's tool in the viewer, and says whether it did
 *
 * Plain terms
 * -----------
 * Leaving a reading is not an error, so a request cut short by it is settled
 * quietly instead of failing. A lock also starts by arming the lesion, and it
 * must not carry on to save without the viewer, so the worker tells its
 * caller whether the viewer actually armed the tool.
 *
 * Technical
 * ---------
 * Returns `true` once the viewer acknowledged, `false` when the request was
 * dropped as teardown (reading page gone, peer destroyed, or a timeout after
 * the page left). Every real failure still throws. `lockUnlockMeasurementsWorker`
 * stops on `false`; the `takeLatest` watcher ignores the value.
 */
export function activateDeactivateMeasurementsWorker(shouldActivate: boolean) {
  return function* ({ payload }: MeasurementsHandleToolAction) {
    try {
      /**
       * Skip the viewer once the reading page is gone (D7-7986)
       *
       * Plain terms
       * -----------
       * Leaving a reading closes the scoring form, and the form's last act is
       * to ask the viewer to stop highlighting the current lesion. The viewer
       * is gone by then, so there is nothing to un-highlight — sending the
       * request only produces a timeout error a few seconds later.
       *
       * Technical
       * ---------
       * `ReadingTaskViewerPage` sets the flag in a layout-effect cleanup, i.e.
       * before the form's unmount-time request reaches D7 (`viewerPresence.ts`).
       * Settled like the peer-destroyed case below: `ok: false, teardown: true`.
       */
      const viewerGone: boolean = yield call(isViewerGone);

      if (viewerGone) {
        resolveFormCall(payload.__callId, { ok: false, teardown: true });

        return false;
      }

      // Derive the real measurement entry from the lesionId; `payload.index` is
      // the form's array position, which has gaps after deletions and collides
      // with persisted toolIndexes. (D7-6906)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const lesionId = (payload as any)?.payload?.lesionId as string | undefined;
      const lesionIdMatch =
        typeof lesionId === 'string' ? /(\d{1,10})$/.exec(lesionId) : null;
      const labelIndex = lesionIdMatch
        ? Math.max(0, Number(lesionIdMatch[1]) - 1)
        : payload.index ?? 0;

      // Override the entry index so both the by-id/by-code lookup and the data
      // sent to the viewer target the corrected entry.
      const config = { ...payload, index: labelIndex };
      const measurement: ReturnType<typeof selectMeasurementByCode> =
        yield* selectMeasurementForPayload(config);

      // On grouped readings the inventory lists the same lesion code once per
      // scoring form, and the form's unscoped by-code lookup can send the OTHER
      // form's config. The viewer's `isBound` check then fails and it arms a
      // second draw over an existing lesion. The resolved row is authoritative:
      // swap in the inventory config its `measurementId` points to. No row or
      // no mismatch → the config passes through unchanged.
      const inventory: ReturnType<
        typeof clinicalTrialInventorySlice.selectors.selectClinicalTrialInventory
      > = yield select(
        clinicalTrialInventorySlice.selectors.selectClinicalTrialInventory
      );

      const rowLabelConfig = measurement?.measurementId
        ? inventory?.measurements?.find(m => m.id === measurement.measurementId)
        : undefined;

      const resolvedConfig = applyFormatOverride(
        rowLabelConfig && rowLabelConfig.id !== config.measurementConfig?.id
          ? { ...config, measurementConfig: rowLabelConfig }
          : config
      );

      const data: MeasurementItem = {
        config: resolvedConfig,
        labelIndex,
        measurement: measurement ?? {},
      };
      const requestViewer = shouldActivate
        ? viewerRpc.requests.measurement.activate
        : viewerRpc.requests.measurement.deactivate;
      yield call(requestViewer, data, {
        causationId: payload.__causationId,
        // Long enough for the viewer's slowest legitimate activate, short
        // enough to answer before the form's own timeout — see
        // `@shared/constants/measurementActivation`.
        timeoutMs: ACTIVATE_DEACTIVATE_TIMEOUT_MS,
      });
      resolveFormCall(payload.__callId, { ok: true });

      return true;
    } catch (error) {
      // Leaving the page destroys the RPC peer and drops this request with
      // -32001 — expected teardown, so settle the form call like a supersede
      // instead of surfacing an activate/deactivate failure.
      if (isPeerDestroyedError(error)) {
        resolveFormCall(payload.__callId, { ok: false, teardown: true });

        return false;
      }

      // Same teardown, one step later: the request left while the page was
      // still up and the viewer disappeared before answering (D7-7986).
      const viewerLeftWhilePending: boolean =
        isRpcTimeoutError(error) && (yield call(isViewerGone));

      if (viewerLeftWhilePending) {
        resolveFormCall(payload.__callId, { ok: false, teardown: true });

        return false;
      }

      // covers both selector failures and peer.request failures
      rejectFormCall(payload.__callId, error);
      throw error;
    } finally {
      // A `takeLatest` supersede (a new activate/deactivate
      // superseding this one, e.g. fast lesion/series/page switch) cancels this
      // generator mid-flight, bypassing the `catch` above (cancellation unwinds
      // through `finally`, not `catch`). Without settling here, the form's
      // pending promise for `payload.__callId` was left unresolved and the form
      // saw a -32000 timeout 15s later.
      //
      // Resolved, not rejected: being superseded is expected navigation
      // (the reader moved to another lesion/series/page), not a failure — the
      // form's own RPC client showed a reject as a hard "activate/deactivate
      // failed" error, which fired on every fast switch. `ok: false` still
      // tells the truth (the activate/deactivate for THIS request never
      // reached the viewer), just without an exception. `resolveFormCall` is
      // idempotent — a no-op if `try`/`catch` already settled the call on the
      // normal completion path.
      const wasCancelled: boolean = yield cancelled();

      if (wasCancelled) {
        resolveFormCall(payload.__callId, { ok: false, superseded: true });
      }
    }
  };
}

export const activateMeasurementsWorker =
  activateDeactivateMeasurementsWorker(true);

export const deactivateMeasurementsWorker =
  activateDeactivateMeasurementsWorker(false);

export function showHideMeasurementsWorker(shouldShow: boolean) {
  return function* ({ payload }: MeasurementsHandleToolAction) {
    const measurements: ReturnType<typeof selectMeasurementsByIds> =
      yield select(selectMeasurementsByIds, [payload.measurementId]);

    const data: MeasurementItem = {
      config: payload,
      labelIndex: payload.index,
      measurement: measurements?.[0] ?? {},
    };
    yield call(
      shouldShow ? viewerRpc.requests.measurement.show : viewerRpc.requests.measurement.hide,
      data
    );
  };
}

export function* createNAMeasurement(
  payload: MeasurementConfig,
  clinicalTrialId: string,
  taskId: string,
  // permanentData carried forward from the lesion this NA row replaces (the
  // caller resolves it from the existing/seed record). The NA create is a POST,
  // so it must re-send that immutable baseline value verbatim on a follow-up NE
  // lock; on a fresh baseline NE there is none yet, so it falls back to `{}` —
  // the BE rejects a null/absent permanentData ("must be of object type").
  permanentData?: MeasurementReqParams['permanentData'],
  // Label id carried forward from the replaced lesion, used when the external
  // scoring form omits `measurementConfig` on an NE/Merged lock (D7-7309).
  fallbackLabelId?: string
) {
  try {
    const measurementLabelId = payload.measurementConfig?.id ?? fallbackLabelId;

    if (!measurementLabelId) {
      throw new Error(
        'Cannot create measurement: missing clinical trial measurement label id'
      );
    }

    const data: MeasurementReqParams = {
      index: 0,
      instrumentId: payload.instrumentConfig?.id ?? undefined,
      instrumentStateJson: {
        annotation: { isLocked: true, highlighted: false },
        measurementState: { toolIndex: 0 },
      },
      instrumentStateText: '',
      // Persist the NE comment from the lock payload so it survives reload like isNA (D7-7132).
      measurementData: {
        data: {},
        isNA: true,
        comment: (payload.payload as { comment?: string } | undefined)?.comment ?? undefined,
      },
      permanentData: permanentData ?? {},
      measurementLabelId,
      measurementName: payload.measurementConfig?.name ?? '',
    };
    const response: Resp<typeof api.createMeasurement> = yield call(
      api.createMeasurement,
      clinicalTrialId,
      taskId,
      data
    );
    yield dispatch(actions.createMeasurementSuccess(response.data));
    return response.data.id;
  } catch (error) {
    console.error(error);

    throw new Error(
      'Error creating NA measurement: ' + (error as Error)?.message
    );
  }
}

export function* deleteNAMeasurement(
  measurementId: string,
  clinicalTrialId: string,
  taskId: string
) {
  try {
    yield call(api.deleteMeasurement, measurementId, clinicalTrialId, taskId);
    yield dispatch(
      actions.deleteMeasurementSuccess({
        clinicalTrialId,
        measurementId,
        taskId,
      })
    );
  } catch (error) {
    console.error(error);

    throw new Error(
      'Error deleting NA measurement' + (error as Error)?.message
    );
  }
}

/**
 * Persist the scoring-form data onto an existing measurement before it is locked.
 *
 * Plain terms
 * -----------
 * Right before a lesion is locked, whatever the reader entered in the scoring
 * form (its status, location, measured values) must be saved onto that lesion.
 * Both the normal "lock the drawing" flow and the "no screenshot" (Absent / NE)
 * flow save the form data the same way — this is that shared save step.
 *
 * Technical
 * ---------
 * Extracted from `lockUnlockMeasurementsWorker` so `screenshotNAWorker` performs
 * the identical pre-lock update (a PUT via `updateMeasurementsWorker`) instead of
 * a divergent copy. The scoring form may attach its schema-conformant data on the
 * tool-action payload itself OR nested under `payload.payload`; both sources are
 * read through `FormSubmittedData`. `permanentData` is spread in only when the
 * form supplied one (omitting it preserves BE immutability). The caller passes
 * the already-resolved `clinicalTrialId` / `taskId` so this helper does not
 * re-derive them.
 */
export function* updateMeasurementFromFormPayload(
  payload: MeasurementsHandleToolAction['payload'],
  measurement: NonNullable<ReturnType<typeof selectMeasurementByCode>>,
  clinicalTrialId: string,
  taskId: string
) {
  const instrumentStateJson = produce(
    measurement.instrumentStateJson ?? {},
    draft => {
      if (draft.measurementState) {
        draft.measurementState.payload = payload.payload;
      }
    }
  );

  const {
    index,
    instrumentStateText,
    measurementId: measurementLabelId,
    measurementName,
    measurementData,
  } = measurement;

  // Prefer the scoring-form-supplied measurementData (schema-conformant:
  // { location, subLocation, isEnhanced, isMeasurable, annotations, sppd, status });
  // fall back to the raw cornerstone shape only if the form did not supply one.
  const { measurementData: formMeasurementData, permanentData: formPermanentData } =
    resolveFormSubmittedData(payload);

  const updatedMeasurement = {
    index,
    instrumentStateText,
    instrumentStateJson,
    measurementLabelId,
    measurementName,
    measurementData: formMeasurementData ?? measurementData,
    ...(formPermanentData ? { permanentData: formPermanentData } : {}),
  };

  // Propagate the PUT outcome so a caller that owns the whole persist (the form `update`
  // worker) can surface a backend failure instead of reporting a false success to the form.
  const persisted: boolean = yield call(
    updateMeasurementsWorker,
    actions.updateMeasurement({
      clinicalTrialId,
      data: updatedMeasurement,
      measurementId: measurement.id ?? '',
      taskId,
    })
  );

  return persisted;
}

function clearRecordGeometry(
  measurement: NonNullable<ReturnType<typeof selectMeasurementByCode>>
) {
  return produce(measurement, draft => {
    const nextIsj = { ...(draft.instrumentStateJson ?? {}), annotations: [] };

    delete nextIsj.annotation;

    if (nextIsj.measurementState) {
      nextIsj.measurementState = {
        ...(nextIsj.measurementState as Record<string, unknown>),
        data: {},
      } as typeof nextIsj.measurementState;
    }

    draft.instrumentStateJson = nextIsj;
  });
}

type FormSubmittedData = {
  measurementData?: MeasurementReqParams['measurementData'];
  permanentData?: MeasurementReqParams['permanentData'];
};

function resolveFormSubmittedData(
  payload: MeasurementsHandleToolAction['payload']
): FormSubmittedData {
  const top = payload as unknown as FormSubmittedData;
  const nested = payload.payload as FormSubmittedData | undefined;

  return {
    measurementData: top.measurementData ?? nested?.measurementData,
    permanentData: nested?.permanentData,
  };
}

function recordHasMeasurement(
  measurement: NonNullable<ReturnType<typeof selectMeasurementByCode>>
): boolean {
  const data = measurement.instrumentStateJson?.measurementState?.data as
    | Record<string, unknown>
    | undefined;

  return !!data && Object.keys(data).length > 0;
}

function formAnnotationHasMeasurement(a?: { instrument?: string }): boolean {
  return resolveToolName(a?.instrument) !== LesionToolNamesEnum.NonTargetLesion;
}

function shouldClearGeometry(
  payload: MeasurementsHandleToolAction['payload'],
  measurement: NonNullable<ReturnType<typeof selectMeasurementByCode>>
): boolean {
  const recordHasShape =
    !!measurement.instrumentStateJson?.measurementState?.uid;
  if (!recordHasShape) {
    return false;
  }

  const { measurementData } = resolveFormSubmittedData(payload);
  const formAnnotations = (
    measurementData as
      | { annotations?: Array<{ instrument?: string }> }
      | undefined
  )?.annotations;

  if (!Array.isArray(formAnnotations)) {
    return false;
  }

  const formHasMeasurement = formAnnotations.some(formAnnotationHasMeasurement);

  return (
    !formHasMeasurement &&
    (formAnnotations.length === 0 || recordHasMeasurement(measurement))
  );
}

// Remove a single drawn shape from the LIVE canvas by its measurementState.uid
// (like removeMeasurementsWorker). Instrument-agnostic — the shape may be a lesion,
// marker, ROI, etc.; we only need its uid. Fire-and-forget (canvas is best-effort).
function* wipeShapeFromCanvas(
  measurement: NonNullable<ReturnType<typeof selectMeasurementByCode>>,
  causationId?: string
) {
  const uid = measurement.instrumentStateJson?.measurementState?.uid;

  if (!uid) {
    return;
  }

  try {
    yield call(viewerRpc.requests.measurement.delete, { uid }, { causationId });
  } catch (e) {
    console.warn('update: viewer annotation removal failed', e);
  }
}

// Persist the reader's form status on the `update` action (the lock flow's pre-lock PUT,
// minus the lock). For a no-measurement status (shouldClearGeometry), also wipe the stale
// drawing from the LIVE canvas AND the persisted record in the same PUT, so a reload can't
// bring it back (D7-7309). takeLatest relies on the form awaiting each `update` before firing
// the next (the awaitable `update` contract), so only one persist is in flight and a stale one
// is never left mid-cancel with its form deferred unresolved.
export function* persistMeasurementUpdateWorker({
  payload,
}: MeasurementsHandleToolAction) {
  try {
    const {
      task,
      subTask,
    }: ReturnType<typeof selectStoreDataRelatedToMeasurements> = yield select(
      selectStoreDataRelatedToMeasurements
    );

    const clinicalTrialId =
      subTask?.clinicalTrialId || task?.clinicalTrialId || '';
    const taskId = subTask?.id || task?.id || '';

    const measurement = yield* selectMeasurementForPayload(payload);

    // Persist only an existing saved record that carries form data. A first-ever
    // draw (no record yet) is created by the normal create path, never here (REQ-6).
    if (measurement?.id && payload.payload) {
      // Exempt split/appended lesions (any cornerstone sub-shape in annotations[]): the form
      // blocks a status change while they have annotations, so the reader clears them manually.
      // A never-split lesion keeps its shape in singular `annotation`, so annotations[] is empty.
      const hasCanvasSubShapes =
        (measurement.instrumentStateJson?.annotations?.length ?? 0) > 0;

      const clearGeometry =
        !hasCanvasSubShapes && shouldClearGeometry(payload, measurement);

      // No-measurement status → wipe the stale drawing from the LIVE canvas AND the
      // persisted record below. A measurable status change leaves the drawing untouched.
      if (clearGeometry) {
        yield* wipeShapeFromCanvas(measurement, payload.__causationId);
      }

      const persisted = yield* updateMeasurementFromFormPayload(
        payload,
        clearGeometry ? clearRecordGeometry(measurement) : measurement,
        clinicalTrialId,
        taskId
      );

      // A rejected PUT must reach the form as a failure, not a silent success — otherwise the
      // reader believes the status saved. updateMeasurementsWorker swallows its own error, so
      // gate on its returned outcome and route failure through the catch (reject + toast).
      if (!persisted) {
        throw new Error('Measurement status update was not persisted');
      }
    }

    resolveFormCall(payload.__callId, { ok: true });

  } catch (error) {
    rejectFormCall(payload.__callId, error);
    yield showErrorToast(error, 'Failed to save measurement status');
    throw error;
  }
}

type ScreenshotLockRollbackParams = {
  annotationUID?: string;
  annotationUIDs: string[];
  clinicalTrialId: string;
  measurementId: string;
  payload: MeasurementsHandleToolAction['payload'];
  scoringCode?: string;
  screenshotError: Error;
  taskId: string;
};

/**
 * Unlock a measurement when its required screenshot upload or confirmation fails.
 * The backend unlock is authoritative for Sign and Submit; viewer and form
 * notifications are best-effort after that rollback succeeds (D7-7653).
 */
export function* rollbackMeasurementLockAfterScreenshotFailure({
  annotationUID,
  annotationUIDs,
  clinicalTrialId,
  measurementId,
  payload,
  scoringCode,
  screenshotError,
  taskId,
}: ScreenshotLockRollbackParams) {
  try {
    yield call(api.unlockMeasurement, measurementId, clinicalTrialId, taskId);

  } catch (rollbackError) {
    const error = new Error(
      'Screenshot upload failed and measurement could not be unlocked.'
    );

    console.error(rollbackError);
    yield dispatch(actions.unlockMeasurementFailure(rollbackError));
    yield call(rejectFormCall, payload.__callId, error);
    yield call(showErrorToast, error, error.message);

    return;
  }

  yield dispatch(
    actions.unlockMeasurementSuccess({
      clinicalTrialId,
      measurementId,
      taskId,
    })
  );

  try {
    yield call(
      viewerRpc.requests.measurement.unlock,
      { annotationUID, annotationUIDs },
      { causationId: payload.__causationId }
    );
  } catch (viewerUnlockError) {
    console.error(viewerUnlockError);
  }

  // The pre-lock save landed before the screenshot failed, so `null` here told
  // the form the lesion had no measured value at all.
  const data: Gen<typeof selectPersistedMeasurementData> = yield call(
    selectPersistedMeasurementData,
    measurementId,
    payload.isNA
  );

  try {
    yield call(
      scoringFormMeasurementsChangeAction,
      scoringCode,
      'unlock_done',
      {
        locked: false,
        code: payload.measurementConfig?.code,
        value: {
          isNA: payload.isNA,
          data,
          index: payload.index,
          measurementId,
        },
      }
    );
  } catch (formUnlockError) {
    console.error(formUnlockError);
  }

  yield dispatch(actions.lockMeasurementFailure(screenshotError));
  yield call(rejectFormCall, payload.__callId, screenshotError);
  yield call(
    showErrorToast,
    screenshotError,
    'Screenshot upload failed. Measurement was unlocked.'
  );

}

// A failed lock/unlock leaves the lesion exactly as it was. The event name already
// reported that reversal; `locked` used to carry the attempted state and contradict it.
export function* notifyLockFailed(
  scoringCode: string | undefined,
  payload: MeasurementsHandleToolAction['payload'],
  shouldLock: boolean
) {
  const value = {
    isNA: payload.isNA,
    data: null,
    index: payload.index,
  };

  yield call(scoringFormMeasurementsChangeAction, scoringCode, 'screenshot', {
    code: payload?.measurementConfig?.code,
    value: { ...value, screenshot: { status: 'FAILED' } },
  });

  yield call(
    scoringFormMeasurementsChangeAction,
    scoringCode,
    shouldLock ? 'unlock_done' : 'lock_done',
    {
      locked: !shouldLock,
      code: payload?.measurementConfig?.code,
      value,
    }
  );
}

// A lock/unlock that took; the value comes from the store, not from the payload.
export function* notifyLockDone(
  scoringCode: string | undefined,
  payload: MeasurementsHandleToolAction['payload'],
  shouldLock: boolean,
  measurementId: string
) {
  const data: Gen<typeof selectPersistedMeasurementData> = yield call(
    selectPersistedMeasurementData,
    measurementId,
    payload.isNA
  );

  yield call(
    scoringFormMeasurementsChangeAction,
    scoringCode,
    shouldLock ? 'lock_done' : 'unlock_done',
    {
      locked: shouldLock,
      code: payload?.measurementConfig?.code,
      value: {
        isNA: payload.isNA,
        data,
        index: payload.index,
        // Surface the measurement id the lock/unlock acted on. For an NA
        // (Not-Evaluable) lock this is the id of the measurement just created
        // (see createNAMeasurement above), which the scoring form has no other
        // way to learn in-session — without it a later NA unlock fires with an
        // empty id and produces a malformed POST .../measurements//unlock that
        // the backend rejects with HTTP 400 (D7-7189).
        measurementId,
      },
    }
  );
}

// Waits for the LAST in-flight write, not the first settle. Two things rule out a
// single `take`: the old `take([create/update Success|Failure])` was unkeyed, so the
// direct `api.*` paths released it without ever raising the count, and under
// `takeQueue` one annotation settling says nothing about another still writing.
export function* waitForMeasurementWrites() {
  let pending: boolean = yield select(
    measurementsSlice.selectors.getWritePending
  );

  while (pending) {
    yield take(isMeasurementWriteSettled);

    pending = yield select(measurementsSlice.selectors.getWritePending);
  }
}

// Read at notify time, not at worker entry: the pre-lock save runs in between and
// `updateMeasurementSuccess` replaces the record with the backend's own copy.
export function* selectPersistedMeasurementData(
  measurementId: string,
  isNA?: boolean
) {
  if (isNA || !measurementId) {
    return null;
  }

  const record: Measurement | undefined = yield select(
    (state: { measurements: MeasurementsState }) =>
      state.measurements.data[measurementId]
  );

  return record?.instrumentStateJson?.measurementState?.data ?? null;
}

/**
 * Lock Unlock Measurements Worker
 * This worker is responsible for locking and unlocking the measurements in DB by calling the backend API and updating the viewer state
 * Also this worker is responsible for creating and deleting N/A (Not Applicable) measurements, in this case annotations are not created in the viewer
 *  - If the measurement is locked, it will be unlocked
 *  - If the measurement is unlocked, it will be locked
 *  - If the measurement is N/A and is unlocked it will be created and deleted previously created measurement and annotation in the viewer and DB
 *  - If the measurement is N/A and is locked it will be deleted from the DB
 * @param shouldLock  - boolean
 * @returns  - Generator
 */
export function lockUnlockMeasurementsWorker(shouldLock: boolean) {
  return function* ({ payload, type }: MeasurementsHandleToolAction) {
    // Hoisted above the try so the `finally` below can close the lock-sequence
    // window no matter where the worker exits (D7-7565). `measurement` itself is
    // resolved inside the try and is not visible from a finally block.
    let lockSequenceId = '';

    // Set once the non-screenshot-lock release below actually fires, so the
    // `finally` backstop (see its own comment) does not send a second,
    // redundant release RPC on the common success path.
    let releasedInputBlock = false;

    try {
    const apiFN = shouldLock ? api.lockMeasurement : api.unlockMeasurement;

    const successFN = shouldLock
      ? actions.lockMeasurementSuccess
      : actions.unlockMeasurementSuccess;

    const failureFN = shouldLock
      ? actions.lockMeasurementFailure
      : actions.unlockMeasurementFailure;

    const viewerReady: boolean = yield select(
      measurementsSlice.selectors.getViewerReady
    );

    if (!viewerReady && !shouldLock) {
      yield take(measurementsSlice.actions.setViewerReady.type);
      yield delay(500);
    }

    const writePending: boolean = yield select(
      measurementsSlice.selectors.getWritePending
    );

    if (writePending) {
      yield race({
        timeout: delay(15000),
        settled: call(waitForMeasurementWrites),
      });
    }

    // D7-7394: wait for an in-flight create/update before re-selecting the
    // measurement. In grouped readings, activating too early can resolve stale
    // measurement state from another visit and navigate the viewer away.
    // Strip __callId so this internal activate does not resolve the lock's
    // form-call deferred early; the lock resolves it at the end.
    //
    // `__blockViewportInput` rides along on every lock, screenshot or not
    // (D7-7455, per review): the viewer freezes scroll/slice-navigation input
    // while handling this awaited ACTIVATE, i.e. BEFORE the update/lock API
    // round trips below — so a fast scroll during those calls cannot move the
    // viewport off the measured slice before a screenshot is captured. For a
    // screenshot-bound lock the viewer lifts the freeze once the capture
    // settles; for a non-screenshot lock there is no capture to wait for, so
    // the LOCK request below carries `releaseInputBlock` and the viewer lifts
    // it as soon as that lands. Either way the blocker's own backstop timer
    // still applies if the host never gets to signal at all.
    const armed: boolean = yield call(activateMeasurementsWorker, {
      type,
      payload: {
        ...payload,
        __callId: undefined,
        ...(shouldLock ? { __blockViewportInput: true } : {}),
      },
    });

    /**
     * Stop a lock/unlock when the reading page is already gone (D7-7986)
     *
     * Plain terms
     * -----------
     * A lock or unlock first brings the lesion up in the viewer. If the reader
     * has already left the reading, there is no viewer to do that, so nothing
     * is saved on the server. A form still open in a detached window is told
     * the lock did not happen, so it never shows the lesion as locked.
     *
     * Technical
     * ---------
     * `activateMeasurementsWorker` returns `false` for teardown (reading page
     * gone, peer destroyed, timeout after the page left) instead of throwing,
     * so the form-driven activate settles quietly. Before D7-7986 those cases
     * threw into the backstop `catch` below, which also skipped the backend
     * calls and rejected the form call; this keeps both outcomes without the
     * watcher-level error log. Rejected rather than resolved: a resolved lock
     * call reads as "locked" to a form that only checks for rejection. No
     * update/lock API call has run yet and the lock window is not open.
     * `releasedInputBlock` is set so the `finally` does not send a release to
     * a viewer that is gone. (`armed` is `undefined` only when this worker is
     * itself cancelled, in which case it never resumes here.)
     */
    if (!armed) {
      releasedInputBlock = true;

      rejectFormCall(
        payload.__callId,
        new Error('Lock/unlock skipped: the viewer left before arming the lesion')
      );

      return;
    }

    const measurement: ReturnType<typeof selectMeasurementByCode> =
      yield* selectMeasurementForPayload(payload);

    const {
      task,
      subTask,
    }: ReturnType<typeof selectDataRelatedToMeasurements> = yield select(
      selectDataRelatedToMeasurements,
      measurement?.instrumentStateJson.annotation,
      measurement?.instrumentStateJson.measurementState
    );

    const scoringCode = findScoringCode(subTask?.visits || task?.visits || []);

    /**
     * Open the lock window BEFORE the pre-lock save (D7-7565).
     *
     * Plain terms
     * -----------
     * From here until the lock finishes, this lesion is "being locked". If the
     * reader nudges its shape in the image at the same moment, the viewer's own
     * save is held back instead of racing ours — otherwise our older snapshot
     * could land last and quietly undo their nudge.
     *
     * Technical
     * ---------
     * Must precede `updateMeasurementFromFormPayload` — that PUT is exactly what
     * the viewer echo races. Lock direction only: the unlock path runs no
     * pre-unlock PUT, so it has nothing to serialize against (bugfix.md scope).
     */
    if (shouldLock && measurement?.id) {
      lockSequenceId = measurement.id;

      yield dispatch(actions.beginLockSequence({ measurementId: lockSequenceId }));
    }

    /////////////////////////////////////////
    // UPDATE MEASUREMENT state to save data from scoring form
    /////////////////////////////////////////
    // ==========================================
    if (
      shouldLock &&
      measurement?.instrumentStateJson?.measurementState &&
      payload.payload
    ) {
      yield* updateMeasurementFromFormPayload(
        payload,
        measurement,
        subTask?.clinicalTrialId || task?.clinicalTrialId || '',
        subTask?.id ?? task?.id ?? ''
      );
    }

    try {
      let measurementId = measurement?.id ?? '';
      const clinicalTrialId =
        subTask?.clinicalTrialId || task?.clinicalTrialId || '';
      const taskId = subTask?.id || task?.id || '';

      /////////////////////////////////////////
      // Handle N/A measurements
      // if measurement is N/A and is unlocked, delete prev and  create a new measurement with empty annotation
      /////////////////////////////////////////
      if (payload.isNA && shouldLock) {
        // The NA lock deletes the drawn row and recreates it. Resolve the row's
        // clinical-trial link (form config, else the replaced row's) BEFORE the
        // delete, so an unresolvable link aborts without destroying the existing
        // row instead of leaving the reader with nothing (D7-7309).
        const naLabelId =
          payload.measurementConfig?.id ?? measurement?.measurementId;

        if (!naLabelId) {
          throw new Error(
            'Cannot lock measurement: missing clinical trial measurement label id'
          );
        }

        if (measurement?.id) {
          yield call(
            deleteNAMeasurement,
            measurement.id,
            clinicalTrialId,
            taskId
          );
          // delete measurement tool annotation from the viewer
          if (measurement.instrumentStateJson.measurementState?.uid) {
            yield call(
              viewerRpc.events.deleteMeasurement,
              measurement.instrumentStateJson.measurementState.uid
            );
          }
        }
        measurementId = yield call(
          createNAMeasurement,
          payload,
          clinicalTrialId,
          taskId,
          // Re-send the carried-forward baseline permanentData (the record we just
          // deleted held it) so a follow-up NE lock satisfies BE immutability;
          // createNAMeasurement falls back to `{}` for a fresh baseline NE.
          measurement?.permanentData,
          // Already-resolved clinical-trial link (guarded above).
          naLabelId
        );
      }

      yield call(apiFN, measurementId, clinicalTrialId, taskId);

      /////////////////////////////////////////
      // Handle N/A measurements
      // if measurement is N/A and is locked, delete the measurement from the DB
      /////////////////////////////////////////
      if (payload.isNA && !shouldLock) {
        yield call(deleteNAMeasurement, measurementId, clinicalTrialId, taskId);
      }

      yield dispatch(successFN({ clinicalTrialId, measurementId, taskId }));

      const annotationUID =
        measurement?.instrumentStateJson?.annotation?.annotationUID;
      // Lock/unlock EVERY annotation of the record, not just the legacy
      // singular — otherwise appended entries stay editable/resizable in the
      // viewer while the lesion shows as locked (D7-6906). `annotationUID` is
      // kept for back-compat; `annotationUIDs` carries the full set.
      const annotationUIDs = Array.from(
        new Set(
          [
            annotationUID,
            ...((measurement?.instrumentStateJson?.annotations ?? []).map(
              a => a.annotationUID
            )),
          ].filter((uid): uid is string => !!uid)
        )
      );
      const requestViewer = shouldLock
        ? viewerRpc.requests.measurement.lock
        : viewerRpc.requests.measurement.unlock;

      // A non-screenshot lock has no capture to wait for, so nothing else
      // ever tells the viewer to lift the freeze it engaged in
      // activateToolWorker — `releaseInputBlock` is that signal.
      // Screenshot-bound locks omit it: the freeze must survive past this
      // call, into the screenshot capture below, and is lifted there instead
      // (`getScreenshotMeasurements`'s `finally`).
      const releaseInputBlock = shouldLock && !payload.needScreenshot;

      yield call(
        requestViewer,
        {
          annotationUID,
          annotationUIDs,
          ...(releaseInputBlock ? { releaseInputBlock } : {}),
        },
        { causationId: payload.__causationId }
      );

      // Mark it sent so the `finally` backstop below skips its own release —
      // this was the primary, prompt one.
      if (releaseInputBlock) {
        releasedInputBlock = true;
      }

      if (payload.needScreenshot && shouldLock) {
        yield spawn(requestScreenshotFromViewer, {
          config: payload,
          labelIndex: payload.index,
          measurement: measurement ?? {},
        });

        const raceResult: {
          ok?: ReturnType<typeof actions.screenshotMeasurementUploadSuccess>;
          fail?: ReturnType<typeof actions.screenshotMeasurementUploadFailure>;
          timeout?: true;
        } = yield race({
          ok: take(actions.screenshotMeasurementUploadSuccess.type),
          fail: take(actions.screenshotMeasurementUploadFailure.type),
          timeout: delay(60000),
        });

        if (!raceResult.ok) {
          const screenshotError =
            raceResult.fail?.payload ?? SCREENSHOT_UPLOAD_TIMEOUT_ERROR;

          yield call(rollbackMeasurementLockAfterScreenshotFailure, {
            annotationUID,
            annotationUIDs,
            clinicalTrialId,
            measurementId,
            payload,
            scoringCode,
            screenshotError,
            taskId,
          });

          return;
        }

        const screenshot = raceResult.ok?.payload ?? null;

        yield call(notifyLockDone, scoringCode, payload, shouldLock, measurementId);

        resolveFormCall(payload.__callId, {
          ok: true,
          locked: true,
          screenshot,
        });

      } else {
        yield call(notifyLockDone, scoringCode, payload, shouldLock, measurementId);

        resolveFormCall(payload.__callId, { ok: true, locked: shouldLock });

      }
    } catch (error) {
      yield call(notifyLockFailed, scoringCode, payload, shouldLock);

      if (payload.needScreenshot) {
        yield spawn(requestScreenshotFromViewer, {
          config: payload,
          labelIndex: payload.index,
          measurement: measurement ?? {},
        });
      }
      yield dispatch(failureFN(error));
      rejectFormCall(payload.__callId, error);
      yield showErrorToast(error, 'Lock/Unlock error occur');
    }
    } catch (error) {
      // A screenshot-bound lock froze viewer input via the activate-before-lock
      // (`__blockViewportInput`, D7-7455). Errors landing here never reach the
      // inner catch's screenshot re-fire, so re-fire it now — the viewer's
      // `getScreenshotMeasurements` releases the freeze in its `finally`.
      // Without this the viewer stays frozen until the blocker's 30s backstop.
      if (shouldLock && payload.needScreenshot) {
        yield spawn(requestScreenshotFromViewer, {
          config: payload,
          labelIndex: payload.index,
          measurement: {},
        });
      }

      // backstop: any throw not already handled (e.g. activate-before-lock or
      // updateMeasurementsWorker) must still settle the form deferred so the
      // form rejects immediately with the real cause.
      // rejectFormCall is idempotent — no-op if already settled by the inner catch.
      rejectFormCall(payload.__callId, error);
      throw error;
    } finally {
      // Close the lock window on EVERY exit path — success, the inner catch, the
      // backstop rethrow, and takeLatest cancellation. A leaked counter would
      // park every later viewer echo for this record behind a 20s timeout
      // (D7-7565).
      if (lockSequenceId) {
        yield dispatch(actions.endLockSequence({ measurementId: lockSequenceId }));
      }

      // A non-screenshot lock throwing before the LOCK RPC above ever ran
      // (updateMeasurementFromFormPayload, the NA delete/create sequence,
      // apiFN itself) never gets the success-path `releaseInputBlock` signal.
      // Release the freeze here as a catch-all — idempotent no-op if it was
      // already released, and swallowed on failure (the blocker's own 30s
      // backstop is the true last resort for a dead postMessage channel).
      if (shouldLock && !payload.needScreenshot && !releasedInputBlock) {
        try {
          yield call(
            viewerRpc.requests.measurement.lock,
            { releaseInputBlock: true },
            { causationId: payload.__causationId }
          );
        } catch (e) {
          console.warn(
            'lockUnlockMeasurementsWorker: input-block release failed',
            e
          );
        }
      }
    }
  };
}

/**
 * Confirm-screenshot worker (no-screenshot lock).
 *
 * Plain words: some lesion statuses are locked WITHOUT taking a picture in the
 * viewer (RANO target Absent/Merged/NE and non-target Absent/NE). For those the
 * form does not ask for a screenshot — it tells the backend "this lock has no
 * screenshot, on purpose". This worker handles that message.
 *
 * The flow:
 *
 *   Form locks an Absent/Merged/NE lesion
 *            │
 *            ▼
 *   Fires `screenshotNA` (NOT `lock`)        ← no viewer round-trip
 *            │
 *      first lock of this lesion?
 *        │              │
 *       yes             no
 *        │              │
 *        ▼              │
 *   create the BE row   │            ← no drawn ROI, so build the row straight
 *   from the payload    │              from the form's schema data
 *        │              │
 *        └──────┬───────┘
 *               ▼
 *   POST …/screenshot/confirm?notAvailable=true   ← "screenshot deliberately absent"
 *
 * Note: this mirrors createNAMeasurement, but it persists the real status data
 * instead of an `isNA` marker. (The `notAvailable` query-param key is the
 * backend contract and is intentionally left as-is.)
 */
export function* screenshotNAWorker({
  payload,
}: MeasurementsHandleToolAction) {
  // Hoisted above the try so the `finally` can close the lock window on every
  // exit path, including the catch-and-rethrow (D7-7565).
  let measurementId = payload.measurementId ?? '';

  // Existing lesion: the update→lock→confirm chain starts right away, so the
  // window opens here. The create branch below has no id yet and opens its own.
  if (measurementId) {
    yield dispatch(actions.beginLockSequence({ measurementId }));
  }

  try {
    const {
      task,
      subTask,
    }: ReturnType<typeof selectStoreDataRelatedToMeasurements> = yield select(
      selectStoreDataRelatedToMeasurements
    );

    const clinicalTrialId =
      subTask?.clinicalTrialId || task?.clinicalTrialId || '';
    const taskId = subTask?.id || task?.id || '';
    const scoringCode = findScoringCode(subTask?.visits || task?.visits || []);

    // First lock for this lesion: create the BE row from the form payload
    // before confirming. The form's schema-conformant data lives under
    // payload.payload.{measurementData, permanentData}.
    if (payload.shouldCreate && !measurementId) {
      const formPayload = payload.payload as
        | {
            measurementData?: MeasurementReqParams['measurementData'];
            permanentData?: MeasurementReqParams['permanentData'];
          }
        | undefined;

      // Guard: a row with no clinical-trial link is accepted by the BE but then
      // 400s the follow-up lock ("relationship is not set", D7-7309).
      if (!payload.measurementConfig?.id) {
        throw new Error(
          'Cannot create measurement: missing clinical trial measurement label id'
        );
      }

      const data: MeasurementReqParams = {
        index: payload.index ?? 0,
        instrumentId: payload.instrumentConfig?.id ?? undefined,
        instrumentStateJson: {
          annotation: { isLocked: true, highlighted: false },
          measurementState: { toolIndex: payload.index ?? 0 },
        },
        instrumentStateText: '',
        measurementData:
          formPayload?.measurementData ??
          ({ data: {} } as MeasurementReqParams['measurementData']),
        // BE rejects a create with null/absent permanentData; default to an
        // empty object when the form payload carries none (see prepareRequestData).
        permanentData: formPayload?.permanentData ?? {},
        measurementLabelId: payload.measurementConfig?.id ?? undefined,
        measurementName: payload.measurementConfig?.name ?? '',
      };

      const response: Resp<typeof api.createMeasurement> = yield call(
        api.createMeasurement,
        clinicalTrialId,
        taskId,
        data
      );
      yield dispatch(actions.createMeasurementSuccess(response.data));
      measurementId = response.data.id;

      // The id only exists now, so this is the earliest point the lock window
      // can open for a freshly created row (D7-7565).
      yield dispatch(actions.beginLockSequence({ measurementId }));
    } else {
      /**
       * Before locking, persist the reader's form data onto the existing
       * measurement — the same pre-lock update the normal lock flow runs
       * (`lockUnlockMeasurementsWorker`).
       *
       * Plain terms
       * -----------
       * Changing an already-drawn target lesion from Present to Absent must save
       * its new (Absent / zeroed) status before the lesion is locked. Without
       * this step the locked row keeps its stale Present data.
       *
       * Technical
       * ---------
       * Fixes D7-7309. `screenshotNA` fires for the existing measurement, so we
       * load it (`selectMeasurementForPayload`) and, when it carries a
       * measurementState and the form sent data, run the shared pre-lock PUT via
       * `updateMeasurementFromFormPayload` before the lock POST below.
       */
      const measurement = yield* selectMeasurementForPayload(payload);

      if (
        measurement?.instrumentStateJson?.measurementState &&
        payload.payload
      ) {
        yield* updateMeasurementFromFormPayload(
          payload,
          measurement,
          clinicalTrialId,
          taskId
        );
      }
    }

    /**
     * Lock the measurement before confirming its "no screenshot" state.
     *
     * Plain terms
     * -----------
     * When a reader marks a lesion as Absent / Merged / NE there is nothing to
     * draw, so the form skips the normal "lock the drawing" round-trip and jumps
     * straight to telling the backend "this lesion has no picture, on purpose".
     * But the backend only accepts that statement for a lesion that is already
     * locked — so we must lock it first, then confirm.
     *
     * Technical
     * ---------
     * screenshotNA fires INSTEAD of `lock` (see the flow diagram above), so the
     * BE lock POST never happens on this path. confirmScreenshotMeasurement
     * (POST …/screenshot/confirm?notAvailable=true) requires a locked row, so we
     * POST the lock here first. We do NOT reuse lockUnlockMeasurementsWorker —
     * it re-runs activate / NA-create / update logic that does not apply here.
     * The matching lockMeasurementSuccess dispatch flips `locked`/`isLocked` in
     * the slice so the store agrees with the BE (the create branch above only
     * dispatched the pre-lock createMeasurementSuccess).
     */
    yield call(api.lockMeasurement, measurementId, clinicalTrialId, taskId);

    yield dispatch(
      actions.lockMeasurementSuccess({ clinicalTrialId, measurementId, taskId })
    );

    // The form only ever fires screenshotNA for a deliberately-absent
    // screenshot, so notAvailable is always true; `?? true` is just a safe
    // default if the field is omitted.
    yield call(
      api.confirmScreenshotMeasurement,
      measurementId,
      clinicalTrialId,
      taskId,
      payload.notAvailable ?? true
    );

    yield call(
      scoringFormMeasurementsChangeAction,
      scoringCode,
      'lock_done',
      {
        locked: true,
        code: payload?.measurementConfig?.code,
        value: {
          isNA: payload.isNA,
          data: null,
          index: payload.index,
          measurementId,
        },
      }
    );

    resolveFormCall(payload.__callId, { ok: true, locked: true });

  } catch (error) {
    // Settle the form deferred first so the form rejects with the real cause,
    // then re-throw so saga error tracking sees the failure (mirrors the
    // backstop in lockUnlockMeasurementsWorker). rejectFormCall is idempotent.
    rejectFormCall(payload.__callId, error);
    yield showErrorToast(error, 'Confirm screenshot error');
    throw error;
  } finally {
    // Closes the window opened at entry OR in the create branch — exactly one of
    // the two ran, so this single decrement always balances it (D7-7565).
    if (measurementId) {
      yield dispatch(actions.endLockSequence({ measurementId }));
    }
  }
}

/**
 * Remove measurements worker
 * This worker is responsible for removing a measurement
 * in case of delete from the ======= [Dynamic Scoring Form] =======
 * @param payload - MeasurementsDeleteAction['payload']
 */

export function* removeMeasurementsWorker({
  payload,
}: MeasurementsHandleToolAction) {
  const apiFN = api.deleteMeasurement;

  const successFN = actions.deleteMeasurementSuccess;

  const failureFN = actions.deleteMeasurementFailure;

  try {
  const measurement: ReturnType<typeof selectMeasurementByCode> =
    yield* selectMeasurementForPayload(payload);

  const { task, subTask }: ReturnType<typeof selectDataRelatedToMeasurements> =
    yield select(
      selectDataRelatedToMeasurements,
      measurement?.instrumentStateJson.annotation,
      measurement?.instrumentStateJson.measurementState
    );
  try {
    const measurementId = measurement?.id ?? '';
    const clinicalTrialId = task?.clinicalTrialId ?? '';
    const taskId = subTask?.id || task?.id || '';
    // if measurement is N/A and is unlocked, delete prev and  create a new measurement with empty annotation
    if (
      measurement?.id &&
      measurement.instrumentStateJson.measurementState?.uid
    ) {
      // delete measurement tool annotation from the viewer via RPC
      yield call(
        viewerRpc.requests.measurement.delete,
        { uid: measurement.instrumentStateJson.measurementState.uid },
        { causationId: payload.__causationId }
      );
    }

    // Call storeMeasurementAction with "delete" event type
    if (measurement?.instrumentStateJson) {
      yield* notifyScoringForm(
        {
          measurement: measurement.instrumentStateJson.measurementState,
          annotation: {
            changeType: 'delete',
            uid:
              measurement.instrumentStateJson.annotation?.annotationUID ?? '',
            annotation: measurement.instrumentStateJson
              .annotation as Partial<AnnotationInstanceObjectType>,
          },
        },
        'delete'
      );
    }

    // D7-7312: a permanent (baseline-carried) measurement cannot be DELETE'd — BE
    // rejects it (`business:measurement_processing`). The cornerstone annotation was
    // already dropped via the viewer RPC above; clean up the record with a PUT
    // (empty annotations, keep the row) instead of a DELETE. `executePermanentCleanup`
    // syncs the store itself via `updateMeasurementSuccess`, so we must NOT also
    // dispatch `deleteMeasurementSuccess` (which would drop the surviving record).
    // This mirrors the viewer-initiated delete branch in `storeMeasurementWorker` —
    // the original D7-7312 host commit covered only that path, not the form's Clear.
    if (measurement?.permanent) {
      yield call(executePermanentCleanup, measurementId);
    } else {
      yield call(apiFN, measurementId, clinicalTrialId, taskId);
      yield dispatch(successFN({ clinicalTrialId, measurementId, taskId }));
    }
    resolveFormCall(payload.__callId, { ok: true });
  } catch (error) {
    yield call(runToast, 'error', 'Failed to delete measurement');
    yield dispatch(failureFN(error));
    rejectFormCall(payload.__callId, error);
  }
  } catch (error) {
    // backstop: selector or other pre-try step threw — settle the deferred so
    // the form rejects immediately. rejectFormCall is idempotent.
    rejectFormCall(payload.__callId, error);
    throw error;
  }
}

/**
 * Prepare request data
 * This function prepares the data that will be sent to the backend API
 * It is responsible for creating, updating and deleting measurements
 * Ii collects the necessary data from the Redux store and provides it to the API
 * @param data  - MeasurementEventData
 * @returns
 */
export function* prepareRequestData(data: MeasurementEventData) {
  const { annotation, measurement } = data ?? {};

  const {
    activeMeasurement,
    currentMeasurement,
    instrumentConfig,
    measurementConfig,
    task,
    subTask,
    visit,
    measurements,
    subTaskVisit,
  }: ReturnType<typeof selectDataRelatedToMeasurements> = yield select(
    selectDataRelatedToMeasurements,
    annotation?.annotation,
    measurement
  );

  let measurementName = measurementConfig?.name ?? '';

  const index = currentMeasurement?.index ?? activeMeasurement?.index ?? 0;

  const indexString = String(index + 1).padStart(2, '0');

  if (measurement && !_.isNumber(measurement.toolIndex)) {
    measurement.toolIndex = index;
  }

  const code = measurementConfig?.code ?? '';

  if (measurementConfig?.namePattern) {
    measurementName = measurementConfig.namePattern.replace('**', indexString);
  }

  const isMeasurementExists = measurements?.some(
    m => m?.instrumentStateJson?.measurementState?.uid === measurement?.uid
  );

  const canBeUpdated = isMeasurementExists && !currentMeasurement?.locked;

  let cleanedAnnotation = annotation?.annotation;

  if (annotation?.annotation?.data?.cachedStats) {
    const cleanedStats = Object.fromEntries(
      Object.entries(annotation.annotation.data.cachedStats).map(
        ([key, value]) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const { pointsInShape, ...rest } = value ?? {};
          return [key, rest];
        }
      )
    );
    cleanedAnnotation = {
      ...annotation.annotation,
      data: {
        ...annotation.annotation.data,
        cachedStats: cleanedStats,
      },
    };
  }

  // BE Value shape is built by the pure helpers `valueFromCachedStats` /
  // `toMeasuredValue` / `naMeasuredValue` (now in `measurementContracts.ts`,
  // consumed by the per-form contracts) — each small, named, unit-tested.

  // Pull cornerstone cachedStats → first slice's measurements.
  const cachedStatsFirst = (() => {
    const stats = cleanedAnnotation?.data?.cachedStats as
      | Record<string, { length?: number; width?: number; unit?: string }>
      | undefined;
    if (!stats) return undefined;
    const firstKey = Object.keys(stats)[0];
    return firstKey ? stats[firstKey] : undefined;
  })();

  // BE schema splits reader-input across two sibling fields:
  //   measurementData: { isMeasurable, annotations, sppd, status }
  //   permanentData:   { location, subLocation, isEnhanced }
  // Cornerstone-driven updates (drag/move annotation) carry only annotation
  // geometry — they must NOT overwrite form-supplied measurementData /
  // permanentData with defaults. Preserve existing reader-supplied values from
  // the stored measurement; overlay only annotations + sppd derived from the
  // new cornerstone state. On initial create there is no currentMeasurement
  // yet, so fall back to schema defaults.
  //
  // The annotations + sppd *shape* is owned by the per-scoring-form contract
  // (measurementContracts.ts); unmapped forms resolve to the byte-identical
  // legacyDefaultContract. Instrument code + numeric `format` are sourced from
  // config there, not hardcoded here.
  const toolName = (measurement as { toolName?: string } | undefined)?.toolName;
  const annotationIndex =
    (currentMeasurement?.index ?? activeMeasurement?.index ?? 0) + 1;

  const contract = resolveMeasurementContract(
    subTaskVisit?.scoringCode || visit?.scoringCode
  );

  const shaped = contract({
    toolName,
    instrumentConfig,
    measurementConfig,
    cachedStatsFirst,
    annotationIndex,
  });

  // A UID-less record already held for this label+index: either a CREATED
  // placeholder, a legacy segmentation row (Liver PDFF migrates segmentation ->
  // Circle ROI, D7-7080), or a previous-visit copy seeded for a follow-up. A
  // follow-up copy arrives with `instrumentStateJson === null` (reader hasn't
  // re-drawn yet) and carries baseline `permanentData` that the BE treats as
  // immutable. Resolving it here lets a freshly drawn ROI UPDATE that record and
  // re-send its permanentData unchanged instead of either creating a
  // disconnected duplicate or omitting permanentData (BE: "Permanent data can't
  // be changed"). Only adopt UID-less records so a real existing measurement is
  // never clobbered.
  const seedMeasurement = measurements?.find(m => {
    if (!measurementConfig?.id || m?.measurementId !== measurementConfig.id) {
      return false;
    }
    if (m?.instrumentStateJson?.annotation?.annotationUID) {
      return false;
    }
    // A previous-visit copy arrives with `instrumentStateJson === null`, so the
    // viewer `toolIndex` is never populated (assembleMeasurements skips null
    // rows). Defaulting it to 0 made the seed match only the first lesion, so
    // drawing the SECOND target lesion found no seed and POSTed a duplicate
    // instead of PUTting the copy. Match the BE top-level `index` first and fall
    // back to the viewer toolIndex (same precedence as selectMeasurementByCode).
    const toolIndex = m?.instrumentStateJson?.measurementState?.toolIndex;
    if (toolIndex !== undefined) {
      return toolIndex === index;
    }
    return m.index === index;
  });

  const existingMeasurementData = (currentMeasurement?.measurementData ??
    {}) as Partial<Measurement['measurementData']> & { status?: string };
  /**
   * Decide which "permanent data" (location / subLocation / isEnhanced) to send.
   *
   * In plain terms
   * --------------
   * Every saved lesion needs a few descriptive details. Where we get them
   * depends on the situation, and the order below matters:
   *   1. The lesion already exists on the server  → reuse exactly what it has.
   *   2. It is a lesion carried over from an earlier visit (a "seed" copy)
   *      → reuse exactly what was carried over.
   *   3. It is a brand-new lesion being drawn for the first time
   *      → use the details the reader just entered in the form.
   *
   * Why the order is not negotiable
   * -------------------------------
   * For an existing or carried-over lesion these details are LOCKED on the
   * server (the backend answers "Permanent data can't be changed"). So cases 1
   * and 2 must win over the form value — we re-send what the server already
   * holds, byte-for-byte. Only a first-time draw (case 3) has no server value
   * yet, and there the freshly captured form value gives the create POST a
   * valid record instead of an empty `{}` (which the backend schema rejects).
   *
   * The drawn shape itself never carries these details, and `currentMeasurement`
   * is undefined on the first draw — hence the fallback chain.
   */
  const existingPermanentData =
    currentMeasurement?.permanentData ??
    seedMeasurement?.permanentData ??
    activeMeasurement?.permanentData;

  /**
   * Decide what measurability ("is this lesion measurable?") to send.
   *
   * In plain terms
   * --------------
   * The reader's own answer must survive the very first save. It used to be
   * thrown away: a lesion the reader had marked Non-Measurable was created as
   * measurable, and the form then read that wrong value back off the server.
   *
   * Technical context
   * -----------------
   * D7-7584. `currentMeasurement` is undefined on a create (the selector
   * matches on an annotation UID the server has never seen), so the first
   * branch never matched and the `true` default fired unconditionally.
   * `activeMeasurement` carries the value the scoring form attached when the
   * reader armed the tool (see `setActiveMeasurement`). The stored server value
   * still wins wherever it exists, so update/PUT behaviour is byte-identical;
   * `true` remains the last resort for forms that send no measurability at all.
   *
   * Why `seedMeasurement` is NOT in this chain
   * ------------------------------------------
   * The sibling `existingPermanentData` resolution above deliberately consults
   * it, because the backend treats permanentData as immutable once written — a
   * carried-forward baseline value MUST be re-sent verbatim. Measurability is
   * the opposite: it is reader-mutable per timepoint (a carried non-target
   * lesion legitimately becomes measurable at a follow-up, D7-7458). Falling
   * back to the seed here would silently re-apply a stale baseline answer over
   * the reader's current one. Do not "align" the two chains.
   */
  const resolvedIsMeasurable = resolveIsMeasurable(
    existingMeasurementData.isMeasurable,
    activeMeasurement?.isMeasurable
  );

  const schemaMeasurementData = {
    isMeasurable: resolvedIsMeasurable,
    ...shaped,
    status: existingMeasurementData.status ?? 'Present',
  };

  const requestData: MeasurementReqParams = {
    index: currentMeasurement?.index ?? activeMeasurement?.index ?? 0,
    instrumentId: instrumentConfig?.id ?? undefined,
    instrumentStateJson: {
      annotation: cleanedAnnotation ?? {},
      measurementState: measurement ?? {},
    },
    instrumentStateText: '',
    measurementData: {
      ...schemaMeasurementData,
      data: measurement?.data ?? {},
      // Persist the DICOM Manufacturer so it survives a reload: resolveVisitManufacturer
      // in the Liver PDFF form falls back to measurementData.manufacturer on refresh,
      // when the live measurementChanges event is no longer present (D7-7080).
      manufacturer: measurement?.manufacturer,
    } as unknown as MeasurementReqParams['measurementData'],
    // permanentData is included only when present (current measurement or a
    // seed copy carried forward to a follow-up). It is OMITTED when absent so the
    // UPDATE/PUT branches never send `{}` over an immutable baseline value; the
    // CREATE/POST sink injects the `{}` default the BE requires (see the create
    // branch in storeMeasurementWorker — BE rejects a null permanentData).
    ...(existingPermanentData ? { permanentData: existingPermanentData } : {}),
    measurementLabelId: measurementConfig?.id ?? undefined,
    measurementName,
  };
  const clinicalTrialId = task?.clinicalTrialId ?? '';
  const taskId = task?.id ?? '';
  const subTaskId = subTask?.id ?? '';
  const measurementId = currentMeasurement?.id ?? '';

  // The seed record's id lets a freshly drawn ROI UPDATE it (persisting the
  // annotationUID under the label's code) instead of creating a disconnected
  // duplicate that leaves lock/select/show-hide and reload-rehydration unable to
  // find the drawn ROI. See `seedMeasurement` above for how it is resolved.
  const placeholderMeasurementId = seedMeasurement?.id ?? '';

  return {
    canBeUpdated,
    // The two halves of `canBeUpdated`, surfaced so a skipped update can say
    // WHICH one refused it (D7-8002).
    isMeasurementExists,
    isLocked: !!currentMeasurement?.locked,
    activeMeasurement,
    requestData,
    clinicalTrialId,
    taskId,
    measurementId,
    placeholderMeasurementId,
    code,
    scoringCode: subTaskVisit?.scoringCode || visit?.scoringCode,
    subTaskId,
  };
}

export function* notifyScoringForm(
  data: MeasurementEventData,
  eventType: MeasurementsStoreActionType
) {
  const {
    code,
    scoringCode,
    measurementId,
    requestData,
  }: Gen<typeof prepareRequestData> = yield call(prepareRequestData, data);

  if (code && scoringCode) {
    yield call(scoringFormMeasurementsChangeAction, scoringCode, eventType, {
      code,
      value: {
        isNA: requestData.measurementData.isNA,
        data: data.measurement?.data,
        index: requestData.index,
        measurementId,
        // DICOM Manufacturer carried on the Circle ROI measurement (D7-7080).
        // The Liver PDFF form reads it live to pick the PDFF scale factor; the
        // visit payload does not populate manufacturer.
        manufacturer: data.measurement?.manufacturer,
      },
    });
  }
}

/**
 *  Store measurement worker
 * This worker is responsible for managing the measurement data between the viewer and the database
 * It is responsible for creating, updating and deleting measurements
 *
 * @param action - MeasurementsStoreAction
 */
export function* storeMeasurementWorker(action: MeasurementsStoreAction) {
  const { data, eventType } = action.payload;

  // D7-7582: only the main (viewer-owning) tab persists a viewer store event.
  // A detached spawn tab has no viewer of its own, yet `VIEWER__STORE_MEASUREMENT`
  // is relayed to it — without this guard both tabs POST the same create and the
  // backend ends up with a duplicate measurement row (the form deletes one copy,
  // the other survives on the canvas after a refresh). The tool-action path is
  // already guarded this way in `toolHandlerWSWorker`.
  const { mainTab } = yield* selectTabCoordinationState();
  if (!mainTab) {
    return;
  }

  // Locked parent → read-only: never enqueue a sub-annotation write for it.
  // Mirrors the single-annotation `canBeUpdated` guard below.
  if (data?.parentMeasurementId && (data?.appendAnnotation || data?.removeAnnotation)) {
    const parent: Measurement | undefined = yield select(
      (state: { measurements: MeasurementsState }) =>
        state.measurements.data[data.parentMeasurementId as string]
    );

    if (parent?.locked) {
      return;
    }
  }

  // Multi-annotation: route appends/removes onto an existing measurement
  // through the per-measurement actionChannel instead of the create/update
  // path so concurrent writes to the same record serialise (R1).
  if (data?.parentMeasurementId && data?.appendAnnotation) {
    yield dispatch(
      actions.enqueueAppendAnnotation({
        measurementId: data.parentMeasurementId,
        entry: data.appendAnnotation.entry,
        annotation: data.appendAnnotation.annotation,
        measurement: data.measurement,
      })
    );
    return;
  }
  if (data?.parentMeasurementId && data?.removeAnnotation) {
    yield dispatch(
      actions.enqueueRemoveAnnotation({
        measurementId: data.parentMeasurementId,
        annotationUID: data.removeAnnotation.annotationUID,
      })
    );
    return;
  }

  const user: ReturnType<typeof authSlice.selectors.getUser> = yield select(
    authSlice.selectors.getUser
  );

  if (user?.selectedRole === UserRoles.IMAGING_SPECIALIST) {
    return;
  }

  const subEntryAnnotation = data?.annotation?.annotation;
  const subEntryUID = subEntryAnnotation?.annotationUID;

  if (eventType === 'update' && !data?.parentMeasurementId && subEntryUID) {
    const parent: Measurement | undefined = yield select(
      (state: { measurements: MeasurementsState }) =>
        Object.values(state.measurements.data).find(m =>
          (m?.instrumentStateJson?.annotations ?? []).some(
            a => a?.annotationUID === subEntryUID
          )
        )
    );

    const entry = (parent?.instrumentStateJson?.annotations ?? []).find(
      a => a?.annotationUID === subEntryUID
    )?._link;

    if (parent) {
      // Same read-only rule the armed sub-entry route applies above.
      if (parent.locked) {
        return;
      }

      // No `_link` to file this shape under. Every writer sets one
      // (`executeAppend`, `promoteLegacySingularAnnotation`), so this means a
      // malformed record — stop rather than fall through, because the path below
      // would persist this one shape and drop its siblings.
      if (!entry) {
        console.warn('storeMeasurementWorker: sub-entry has no _link; update dropped', {
          measurementId: parent.id,
          annotationUID: subEntryUID,
        });
        return;
      }

      yield dispatch(
        actions.enqueueAppendAnnotation({
          measurementId: parent.id,
          entry,
          annotation: subEntryAnnotation,
          measurement: data.measurement,
        })
      );
      return;
    }
  }

  const {
    clinicalTrialId,
    measurementId,
    placeholderMeasurementId,
    requestData,
    scoringCode,
    code,
    taskId: _taskId,
    canBeUpdated,
    isMeasurementExists,
    isLocked,
    subTaskId,
  }: Gen<typeof prepareRequestData> = yield call(prepareRequestData, data);

  const taskId = subTaskId || _taskId;

  // Awaited, not dispatched: the outcome used to come from an unkeyed `take` that any
  // other measurement's save could release (D7-8035). Stays `undefined` only for an
  // `eventType` outside create/update/delete, which the tail deliberately lets through.
  let writeSucceeded: boolean | undefined;

  // create, update or delete measurement by Backend API
  if (eventType === 'create') {
    // If the segment already has a UID-less placeholder/legacy record, UPDATE it so
    // the drawn ROI's annotationUID is persisted under the segment's code instead of
    // creating a disconnected duplicate (see prepareRequestData).
    if (placeholderMeasurementId) {
      writeSucceeded = yield call(
        updateMeasurementsWorker,
        actions.updateMeasurement({
          measurementId: placeholderMeasurementId,
          clinicalTrialId,
          taskId,
          data: requestData,
        })
      );
    } else {
      writeSucceeded = yield call(
        createMeasurementsWorker,
        actions.createMeasurement({
          clinicalTrialId,
          taskId,
          /**
           * Saving a brand-new lesion (HTTP POST).
           *
           * Plain terms: the server will not accept a new lesion unless its
           * descriptive details are filled in, so we make sure they are present
           * here before sending.
           *
           * Technical: the backend rejects a create whose `permanentData` is
           * null/absent ("must be of object type, but actual type is null") and
           * also validates it against `permanentData.schema.json`
           * ({ location, subLocation, isEnhanced } all required).
           * `prepareRequestData` has already resolved a valid value (from the
           * scoring form captured on activate, or a carried-forward seed). The
           * trailing `?? {}` is a defensive guard ONLY — in the normal flow it
           * never fires, and if it ever did the empty object would fail schema
           * validation (a signal that the form value was lost upstream). The
           * PUT/update branches deliberately omit `permanentData` so they can
           * never overwrite the locked details of an existing lesion.
           */
          data: { ...requestData, permanentData: requestData.permanentData ?? {} },
        })
      );
    }
  }
  if (eventType === 'update') {
    if (!canBeUpdated) {
      console.warn('storeMeasurementWorker: update skipped', {
        measurementId,
        isMeasurementExists,
        isLocked,
      });
      return;
    }

    /**
     * Fire now, or queue behind an in-flight lock (D7-7565).
     *
     * Plain terms
     * -----------
     * Normally the reader's drag is saved immediately. But if that same lesion
     * is in the middle of being locked, saving now would race the lock's own
     * save — and the loser's data wins by accident. In that case the save is
     * parked and replayed once the lock has finished.
     *
     * Technical
     * ---------
     * `canBeUpdated` above reads `currentMeasurement.locked`, which only flips
     * after the lock REST call resolves — it is blind to the whole
     * update→lock→confirm window. `lockPendingByMeasurement` is the in-flight
     * marker for that window; when set, the write goes onto the same
     * per-measurement `actionChannel` that already serializes append/remove
     * (`measurementActionCoordinator`), which re-checks `locked` before firing.
     */
    const lockPending: number = yield select(
      (state: { measurements: MeasurementsState }) =>
        state.measurements.lockPendingByMeasurement[measurementId] ?? 0
    );

    const updatePayload = {
      measurementId,
      clinicalTrialId,
      taskId,
      data: requestData,
    };

    // The queued PUT settles long after this worker returns, so `runQueuedUpdate` owns
    // the notification too — telling the form here would report an unlanded value.
    if (lockPending > 0) {
      yield dispatch(
        actions.enqueueUpdateMeasurement({ ...updatePayload, eventData: data })
      );

      return;
    }

    writeSucceeded = yield call(
      updateMeasurementsWorker,
      actions.updateMeasurement(updatePayload)
    );
  }
  if (eventType === 'delete') {
    const uid = requestData.instrumentStateJson?.measurementState?.uid;

    if (!measurementId) {
      // Measurement was never saved to DB (drawn without form activation) — just clean up the viewer
      if (uid) {
        yield call(viewerRpc.events.deleteMeasurement, uid);
      }
      return;
    }

    /**
     * Permanent (baseline-carried) measurement — clean up, never delete (D7-7312).
     *
     * Plain terms
     * -----------
     * A lesion copied from baseline exists in every follow-up visit and the
     * server refuses to delete it. So when the reader removes its drawn
     * measurement on a follow-up (right-click → Delete, or the form's Clear), we
     * clear the drawn shape and update the record so it stays — ready to be
     * re-measured — and drop the annotation from the image, instead of deleting.
     *
     * Technical
     * ---------
     * BE rejects DELETE on a permanent measurement but ACCEPTS a PUT update.
     * `executePermanentCleanup` empties the annotations and PUTs the reduced
     * record; the `deleteMeasurement` path below runs only for non-permanent
     * records. The form is still notified with the `delete` event so its cell
     * clears, and the viewer annotation is dropped, matching the delete UX.
     */
    const targetMeasurement: Measurement | undefined = yield select(
      (state: { measurements: MeasurementsState }) => state.measurements.data[measurementId]
    );

    if (targetMeasurement?.permanent) {
      yield call(executePermanentCleanup, measurementId);

      if (code && scoringCode) {
        yield call(scoringFormMeasurementsChangeAction, scoringCode, eventType, {
          code,
          value: {
            isNA: requestData.measurementData.isNA,
            data: data.measurement?.data,
            index: requestData.index,
            measurementId,
          },
        });
      }

      if (uid) {
        yield call(viewerRpc.events.deleteMeasurement, uid);
      }

      return;
    }

    writeSucceeded = yield call(
      deleteMeasurementsWorker,
      actions.deleteMeasurement({
        measurementId,
        clinicalTrialId,
        taskId,
      })
    );

    // A failed DELETE used to park this worker on a `take` that its own failure could
    // not satisfy. Leave the record and the annotation alone instead.
    if (!writeSucceeded) {
      return;
    }

    // delete measurement tool annotation from the viewer
    if (code && scoringCode) {
      yield call(scoringFormMeasurementsChangeAction, scoringCode, eventType, {
        code,
        value: {
          isNA: requestData.measurementData.isNA,
          data: data.measurement?.data,
          index: requestData.index,
          measurementId,
        },
      });
    }
    if (uid) {
      yield call(viewerRpc.events.deleteMeasurement, uid);
    }
  }

  // A failed write must not reach the form: that is how a value lands in scoring with
  // an empty measurementId and no record behind it (D7-8035).
  if (writeSucceeded === false) {
    console.warn('storeMeasurementWorker: write failed, form not notified', {
      measurementId,
      eventType,
    });

    return;
  }

  // For delete operations, we've already notified the scoring form in the delete case
  if (eventType !== 'delete') {
    yield call(notifyScoringForm, data, eventType);
  }
}

export function* createMeasurementsWorker(a: MeasurementsCreateAction) {
  yield dispatch(actions.setMeasurementWritePending(true));

  try {
    const { clinicalTrialId, data, taskId } = a.payload;
    const response: Resp<typeof api.createMeasurement> = yield call(
      api.createMeasurement,
      clinicalTrialId,
      taskId,
      data
    );
    yield dispatch(
      actions.createMeasurementSuccess(response.data)
    );

    return true;
  } catch (error) {
    yield dispatch(actions.createMeasurementFailure(error));

    return false;
  } finally {
    // `finally`, not the terminal actions: a cancelled write reaches neither,
    // and a flag stuck true makes every later lock wait out the race below.
    yield dispatch(actions.setMeasurementWritePending(false));
  }
}

// All three write workers answer the same question — did it reach the backend. The
// created record itself is already in the store via `createMeasurementSuccess`.
export function* updateMeasurementsWorker(a: MeasurementsUpdateAction) {
  yield dispatch(actions.setMeasurementWritePending(true));

  try {
    const { clinicalTrialId, data, taskId, measurementId } = a.payload;
    const response: Resp<typeof api.updateMeasurement> = yield call(
      api.updateMeasurement,
      measurementId,
      clinicalTrialId,
      taskId,
      data
    );
    yield dispatch(actions.updateMeasurementSuccess(response.data));

    return true;
  } catch (error) {
    yield dispatch(actions.updateMeasurementFailure(error));

    return false;
  } finally {
    yield dispatch(actions.setMeasurementWritePending(false));
  }
}

/**
 * Delete measurement worker
 * This worker is responsible for deleting a measurement
 * used by StoreMeasurementWorker in case of delete ===== [from the OHIF viewer] ======
 * @param a - MeasurementsDeleteAction
 */
export function* deleteMeasurementsWorker(a: MeasurementsDeleteAction) {
  try {
    const { clinicalTrialId, taskId, measurementId } = a.payload;
    yield call(api.deleteMeasurement, measurementId, clinicalTrialId, taskId);
    yield dispatch(actions.deleteMeasurementSuccess(a.payload));

    return true;
  } catch (error) {
    yield dispatch(actions.deleteMeasurementFailure(error));
    yield call(runToast, 'error', 'Failed to delete measurement');

    return false;
  }
}

/**
 * Keep only measurements that carry at least one drawn annotation, for the viewer
 * revive that repaints saved ROIs after a reload.
 *
 * Plain terms
 * -----------
 * A "Split into multiple lesions" lesion stores its sub-lesions as a LIST of
 * annotations, with no single primary one. The old check looked only for a single
 * primary annotation, so on reload a split lesion looked "empty" and its drawn
 * sub-lesions were never sent back to the image — they disappeared until re-drawn.
 *
 * Technical
 * ---------
 * A D7-6906 multi-annotation record keeps its entries in
 * `instrumentStateJson.annotations[]` and may have NO legacy singular
 * `instrumentStateJson.annotation`. Filtering on the singular alone dropped such
 * records from `reviveMeasurementsAction`, so the viewer's MeasurementService
 * received nothing (`getMeasurements()` === []) and the annotations never
 * rehydrated. Include a record when it has EITHER the singular annotation OR a
 * non-empty `annotations[]` (D7-7388).
 */
export function onlyWithAnnotations(measurements: Measurement[]) {
  return measurements.filter(
    m =>
      !!m.instrumentStateJson?.annotation?.annotationUID ||
      (m.instrumentStateJson?.annotations?.length ?? 0) > 0
  );
}

export function* getScreenshotMeasurementsWorker(
  action: MeasurementsScreenshotStoreAction
) {
  const { data, eventType } = action.payload;

  // D7-7582: only the main (viewer-owning) tab uploads the capture. Redundant
  // since the reply became an addressed RPC response, kept as a cheap net.
  const { mainTab } = yield* selectTabCoordinationState();
  if (!mainTab) {
    return;
  }

  const {
    task,
    subTask,
  }: ReturnType<typeof selectStoreDataRelatedToMeasurements> = yield select(
    selectStoreDataRelatedToMeasurements
  );

  const measurementState: ReturnType<typeof selectMeasurementsState> =
    yield select(selectMeasurementsState);

  const currentMeasurement = Object.values(measurementState?.data ?? {}).find(
    m => measurementState?.activeMeasurement?.index === m?.index
  );
  const [screenshotData]: ReturnType<typeof selectMeasurementScreenshot> =
    yield select(selectMeasurementScreenshot);

  if (!task?.id) {
    throw new Error('Task id is empty.');
  }
  const taskId = subTask?.id || task?.id;

  const scoringCode = findScoringCode(subTask?.visits || task?.visits || []);

  if (!scoringCode) {
    return;
  }

  const {
    screenshot: screenshotBase64,
    config: {
      measurementConfig: { clinicalTrialId, code },
      isNA,
      index,
    },

    measurement: { id, measurementData },
  } = data;
  const screenshot: Blob = yield call(base64ToBlob, screenshotBase64);

  let screenshotResponse;

  try {
    let url = screenshotData?.downloadScreenshotUrl;
    let status = screenshotData?.status;

    if (!url) {
      // D7-7991: the presigned upload URL is ready synchronously on the very
      // first GET in the common case, so check immediately instead of
      // blocking on a fixed sleep first — that alone was adding ~5-6s of
      // dead time to every lock before the upload even started. Only back
      // off between retries, for the rare case the URL genuinely isn't
      // ready yet.
      while (status !== 'CREATED') {
        const getResponse: Resp<typeof screenshotApi.getReaderScreenshot> =
          yield call(
            screenshotApi.getReaderScreenshot,
            clinicalTrialId,
            taskId,
            id ?? ''
          );
        url = getResponse?.data?.uploadSignUrl;
        status = getResponse?.data?.status;
        yield dispatch(actions.screenshotMeasurementSuccess(getResponse.data));
        if (status !== 'CREATED') {
          yield delay(1000);
        }
      }
    }
    // put
    yield call(screenshotApi.setUploadScreenshot, screenshot, url as string);
    // NOTE: use this call for testing
    const readerScreenshot: Resp<typeof screenshotApi.setReaderScreenshot> =
      yield call(
        screenshotApi.setReaderScreenshot,
        id ?? '',
        taskId,
        clinicalTrialId
      );
    screenshotResponse = readerScreenshot.data;

    yield dispatch(actions.screenshotMeasurementSuccess(readerScreenshot.data));
    yield dispatch(
      actions.screenshotMeasurementUploadSuccess(readerScreenshot.data)
    );
  } catch (error) {
    console.error(error);
    yield call(
      scoringFormMeasurementsChangeAction,
      scoringCode,
      eventType ?? 'screenshot_done',
      {
        code,
        value: {
          isNA: isNA,
          data: measurementData?.data,
          label:
            currentMeasurement?.instrumentStateJson?.measurementState
              ?.seriesLabel,
          index: index,
          screenshot: screenshotResponse ?? { status: 'FAILED' },
        },
      }
    );
    yield dispatch(actions.screenshotMeasurementUploadFailure(error));
    yield showErrorToast(error, 'Make screenshot error');
  }

  if (code && scoringCode && !_.isEmpty(screenshotResponse)) {
    yield call(
      scoringFormMeasurementsChangeAction,
      scoringCode,
      eventType ?? 'screenshot_done',
      {
        code,
        locked: currentMeasurement?.locked,
        value: {
          isNA: isNA,
          data: measurementData?.data,
          index: index,
          screenshot: screenshotResponse,
          label:
            currentMeasurement?.instrumentStateJson?.measurementState
              ?.seriesLabel,
        },
      }
    );
  }
}

/**
 * Defensive de-dup for legacy multi-annotation records (D7-6906).
 *
 * An earlier bug routed a resize of an already-appended annotation as a NEW
 * append, pushing a fresh entry for the SAME annotationUID instead of updating
 * in place. Affected records accumulate phantom duplicate entries (one
 * annotationUID occupying e.g. entries #4/#5/#6) and a runaway aggregate
 * Diameter.
 *
 * Collapse to one entry per annotationUID (keeping the first/lowest entry),
 * drop the schema annotations for the removed entries, and recompute the
 * aggregate so such records self-heal on load. The cleaned record is persisted
 * on the reader's next edit (executeAppend/executeRemove PUT the de-duped
 * state). No-op for already-clean records.
 *
 * Mutates the immer draft in place; returns true when it changed anything.
 */
export function dedupeMeasurementAnnotations(
  m: Measurement,
  schema?: ReturnType<typeof getSchema>
): boolean {
  const isj = m.instrumentStateJson;
  const annotations = isj?.annotations;
  if (!annotations || annotations.length < 2) {
    return false;
  }

  const seenUIDs = new Set<string>();
  const keptEntries = new Set<string>();
  const deduped: AnnotationInGroup[] = [];
  for (const a of annotations) {
    const uid = a.annotationUID;
    if (uid && seenUIDs.has(uid)) {
      continue; // phantom duplicate of an annotation already kept
    }
    if (uid) {
      seenUIDs.add(uid);
    }
    deduped.push(a);
    if (a._link) {
      keptEntries.add(`${a._link.instrument}#${a._link.index}`);
    }
  }

  if (deduped.length === annotations.length) {
    return false; // already clean
  }

  const droppedCount = annotations.length - deduped.length;
  isj.annotations = deduped;

  const data = m.measurementData?.data as MeasurementDataPayload | undefined;
  if (data?.annotations) {
    data.annotations = data.annotations.filter(s =>
      keptEntries.has(`${s.instrument}#${s.index}`)
    );
    if (schema) {
      Object.assign(data, schema.computeAggregates(data.annotations));
    }
  }
  if (schema) {
    m.instrumentStateText = schema.computeStateText(m);
  }

  console.warn(
    `[measurements] Healed legacy record ${m.id}: dropped ${droppedCount} ` +
      `duplicate annotation entry(s); ${deduped.length} kept. Persists on next edit.`
  );

  return true;
}

export function* assembleMeasurements() {
  const {
    task,
    subTask,
    visit,
    subTaskVisit,
  }: ReturnType<typeof selectStoreDataRelatedToMeasurements> = yield select(
    selectStoreDataRelatedToMeasurements
  );

  const scoringCode =
    subTaskVisit?.scoringCode ||
    visit?.scoringCode ||
    findScoringCode(subTask?.visits || task?.visits || []);
  const schema = scoringCode ? getSchema(scoringCode) : undefined;

  const currentVisit = subTaskVisit || visit;

  const visitMeasurements: Measurement[] = (currentVisit?.measurements
    ?.measurements ?? []) as unknown as Measurement[];
  const isAdjudicator = (currentVisit?.scores ?? []).length > 0;
  const adjudicatorMeasurements: Measurement[] = (currentVisit?.scores
    ?.map(s => s.measurements?.measurements ?? [])
    .flat() ?? []) as unknown as Measurement[];

  const sourceMeasurements: Measurement[] = isAdjudicator
    ? adjudicatorMeasurements
    : visitMeasurements;

  if (!sourceMeasurements || sourceMeasurements.length === 0) {
    return;
  }

  const assembledMeasurements: Measurement[] = produce(
    sourceMeasurements,
    draft => {
      draft.forEach(m => {
        // Previous-visit copies arrive with instrumentStateJson === null
        // (reader hasn't re-drawn the annotation on this visit yet). Skip the
        // assemble step for those — they exist only to seed lesion card
        // metadata (name + permanentData) on the FE side and must not touch
        // the viewer measurements store, otherwise reviveMeasurements crashes
        // on null.measurementState and measurementsLoaded never flips to true.
        if (!m?.instrumentStateJson) {
          return;
        }

        // Self-heal legacy records that accumulated phantom duplicate entries
        // before the upsert-by-UID fix (D7-6906). Runs before flag-stamping so
        // the remaining steps operate on the de-duped array.
        dedupeMeasurementAnnotations(m, schema);

        // Stamp visibility/lock/highlight flags on every cornerstone annotation
        // — both the legacy singular and any entries in annotations[]. For
        // multi-annotation records the array entries were previously left in
        // an inconsistent state at carry-forward.
        if (m.instrumentStateJson.annotation) {
          m.instrumentStateJson.annotation.isVisible = !isAdjudicator;
          m.instrumentStateJson.annotation.isLocked = m.locked;
          m.instrumentStateJson.annotation.highlighted = false;
        }
        for (const a of m?.instrumentStateJson?.annotations ?? []) {
          a.isVisible = !isAdjudicator;
          a.isLocked = m.locked;
          a.highlighted = false;
        }

        if (
          !m.instrumentStateJson.measurementState &&
          (m.measurementData as unknown as { displayText?: unknown })
            .displayText !== undefined
        ) {
          // Fallback: reuse measurementData shape when backend provided legacy structure
          m.instrumentStateJson.measurementState =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (m.measurementData as unknown as any) || {};
        } else {
          m.instrumentStateJson.measurementState =
            m.instrumentStateJson.measurementState ?? {};
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (m.instrumentStateJson.measurementState as any).toolIndex = m.index;
      });
    }
  );

  yield dispatch(actions.reviveMeasurements(assembledMeasurements));

  const readingTaskId = subTask?.id || task?.id || '';

  yield call(viewerRpc.requests.measurement.revive, {
    measurements: onlyWithAnnotations(assembledMeasurements),
    readingTaskId,
  });
}

export function* requestScreenshotFromViewer(params: {
  config: unknown;
  labelIndex: unknown;
  measurement: unknown;
}) {
  try {
    const data: unknown = yield call(viewerRpc.requests.measurement.screenshot, params);

    yield call(getScreenshotMeasurementsWorker, {
      payload: { data },
    } as never);
  } catch (error) {
    console.error('[measurements] viewer.measurement.screenshot failed', error);
  }
}

export function* sendScreenshotMeasurementsWorkerToViewer({
  payload,
}: MeasurementsHandleToolAction) {
  const measurement: ReturnType<typeof selectMeasurementByCode> =
    yield* selectMeasurementForPayload(payload);

  yield spawn(requestScreenshotFromViewer, {
    config: payload,
    labelIndex: payload.index,
    measurement: measurement ?? {},
  });
}

export function* seriesChangedWorker(
  action: PayloadAction<ViewportSeriesData[]>
) {
  const payload = action.payload;

  const {
    task,
    subTask,
  }: ReturnType<typeof selectStoreDataRelatedToMeasurements> = yield select(
    selectStoreDataRelatedToMeasurements
  );

  const scoringCode = findScoringCode(subTask?.visits || task?.visits || []);

  if (scoringCode) {
    scoringFormSeriesChangedAction(scoringCode, payload);
  }
  yield dispatch(formManagementActions.setLastSeries(payload));
}

function* watchViewerReady() {
  while (true) {
    yield take(Rpc.Events.Viewer.READY);
    yield put(actions.setViewerReady(true));
  }
}

/**
 * Tagged error for client-side schema-validation failures. The worker reads
 * the `retriable` flag to decide whether to populate the retry buffer.
 * Validation failures aren't retriable because the same payload would fail
 * the same check again; transient backend errors are.
 */
export class SchemaValidationError extends Error {
  readonly retriable = false as const;
  constructor(message: string) {
    super(message);
    this.name = 'SchemaValidationError';
  }
}

/**
 * Tagged error for deterministic, structural pre-flight failures — the target
 * measurement is missing from the store, or the clinicalTrialId / taskId context
 * is absent. Like a schema rejection these fail identically on every retry, so
 * they must be non-retriable: marking them retriable surfaces a Retry banner that
 * can never succeed and loops forever (D7-6906).
 */
export class StructuralError extends Error {
  readonly retriable = false as const;
  constructor(message: string) {
    super(message);
    this.name = 'StructuralError';
  }
}

type QueueableAction = PayloadAction<
  | EnqueueAppendAnnotationPayload
  | EnqueueRemoveAnnotationPayload
  | EnqueueUpdateMeasurementPayload
  | MeasurementsHandleToolAction['payload']
>;

type QueuedActionHandler = (
  action: QueueableAction
) => Generator<unknown, unknown, unknown>;

const isAppendAction = (action: QueueableAction): boolean =>
  action.type === actions.enqueueAppendAnnotation.type;

const isRemoveAction = (action: QueueableAction): boolean =>
  action.type === actions.enqueueRemoveAnnotation.type;

export const getQueueMeasurementId = (action: QueueableAction): string => {
  const payload = action.payload as MeasurementsHandleToolAction['payload'];
  const measurementConfigId =
    payload.measurementConfig?.id ??
    payload.measurementConfig?.code ??
    'measurement';

  return payload.measurementId || `${measurementConfigId}:${payload.index ?? 0}`;
};

// The annotation uid is the only identity available before the worker resolves the
// record; `''` groups the keyless events into one queue instead of interleaving them.
export const getStoreMeasurementChannelId = (
  action: MeasurementsStoreAction
): string => {
  const { data } = action.payload;
  const measurement = data?.measurement as
    | { uid?: string }
    | string
    | undefined;

  const uid = typeof measurement === 'string' ? measurement : measurement?.uid;

  return (
    uid ||
    data?.removeAnnotation?.annotationUID ||
    data?.parentMeasurementId ||
    ''
  );
};

/**
 * Hold a dequeued update until the record's lock sequence has settled.
 *
 * Plain terms
 * -----------
 * The reader's nudge was parked because a lock was running. This waits for that
 * lock to report "done" before letting the nudge be saved.
 *
 * Technical
 * ---------
 * D7-7565. A single bounded wait, not a retry loop — the same shape as the
 * `writePending` wait in `lockUnlockMeasurementsWorker`: it
 * proceeds either way, so a cancelled or wedged lock sequence times out after
 * 20s instead of stalling this measurement's channel worker (and everything
 * queued behind it) forever. The `take` is filtered to THIS measurementId so an
 * unrelated record's `endLockSequence` cannot wake it early; the caller's
 * `locked` re-check is what guards against firing into a completed lock.
 */
export function* waitForLockSequenceToSettle(measurementId: string) {
  const lockPending: number = yield select(
    (state: { measurements: MeasurementsState }) =>
      state.measurements.lockPendingByMeasurement[measurementId] ?? 0
  );

  if (lockPending > 0) {
    yield race({
      cleared: take(
        (a: { type: string; payload?: { measurementId?: string } }) =>
          a.type === actions.endLockSequence.type &&
          a.payload?.measurementId === measurementId
      ),
      timeout: delay(20000),
    });
  }
}

export function* runQueuedUpdate(payload: EnqueueUpdateMeasurementPayload) {
  yield call(waitForLockSequenceToSettle, payload.measurementId);

  /**
   * Re-check the lock at dequeue time, not only at enqueue time.
   *
   * Plain terms: if the lesion ended up locked while this save was waiting, the
   * save is dropped — a locked lesion is read-only, exactly as before the fix.
   *
   * Technical: preserves regression case 3.3. `storeMeasurementWorker`'s
   * `canBeUpdated` was evaluated before the wait, so it cannot speak for the
   * post-lock state.
   */
  const stillUpdatable: boolean = yield select(
    (state: { measurements: MeasurementsState }) =>
      !state.measurements.data[payload.measurementId]?.locked
  );

  if (!stillUpdatable) {
    return;
  }

  const writeSucceeded: boolean = yield call(
    updateMeasurementsWorker,
    actions.updateMeasurement(payload)
  );

  // This worker owns the echo end to end, so a failure here has to stop the form
  // too — the same gate `storeMeasurementWorker` applies on its direct path.
  if (!writeSucceeded) {
    return;
  }

  yield call(notifyScoringForm, payload.eventData, 'update');
}

const handleToolAction = (
  worker: WatcherWorker<MeasurementsHandleToolAction>
): QueuedActionHandler =>
  function* queuedToolActionHandler(action) {
    yield call(
      keepWatcherAlive(worker),
      action as MeasurementsHandleToolAction
    );
  };

const handlePayloadAction = <P>(
  worker: (payload: P) => Generator<unknown, unknown, unknown>
): QueuedActionHandler =>
  function* queuedPayloadActionHandler(action) {
    yield call(worker, action.payload as P);
  };

function* queuedMeasurementRemoveWorker(action: QueueableAction) {
  const measurementId = getQueueMeasurementId(action);
  const isLocked: boolean = yield select(
    (state: { measurements: MeasurementsState }) =>
      state.measurements.data[measurementId]?.locked ?? false
  );

  if (isLocked) {
    const error = new Error('Measurement cannot be deleted while locked');

    yield dispatch(actions.deleteMeasurementFailure(error));
    rejectFormCall(
      (action.payload as MeasurementsHandleToolAction['payload']).__callId,
      error
    );
    return;
  }

  yield call(
    keepWatcherAlive(removeMeasurementsWorker),
    action as MeasurementsHandleToolAction
  );
}

/**
 * Route every serialized action to its worker.
 *
 * Plain terms
 * -----------
 * Operations for one lesion must finish in the order in which the reader
 * triggered them. Adding a new queued operation requires one mapping entry.
 *
 * Technical
 * ---------
 * The mapping is also the source of `QUEUEABLE_ACTION_TYPES`, keeping channel
 * registration and dispatch aligned. Tool actions retain watcher-level error
 * isolation; annotation payload actions retain their dedicated retry handling.
 */
const QUEUED_ACTION_HANDLERS: Record<string, QueuedActionHandler> = {
  [actions.enqueueAppendAnnotation.type]: handlePayloadAction<
    EnqueueAppendAnnotationPayload
  >(executeAppend),
  [actions.enqueueRemoveAnnotation.type]: handlePayloadAction<
    EnqueueRemoveAnnotationPayload
  >(executeRemove),
  [actions.enqueueUpdateMeasurement.type]: handlePayloadAction<
    EnqueueUpdateMeasurementPayload
  >(runQueuedUpdate),
  [actions.lockMeasurement.type]: handleToolAction(
    lockUnlockMeasurementsWorker(true)
  ),
  [actions.unlockMeasurement.type]: handleToolAction(
    lockUnlockMeasurementsWorker(false)
  ),
  [actions.removeMeasurement.type]: queuedMeasurementRemoveWorker,
  [actions.persistMeasurementUpdate.type]: handleToolAction(
    persistMeasurementUpdateWorker
  ),
  [actions.screenshotNAMeasurement.type]: handleToolAction(screenshotNAWorker),
};

export const QUEUEABLE_ACTION_TYPES = Object.keys(QUEUED_ACTION_HANDLERS);

function* runEnqueuedAction(action: QueueableAction) {
  const handler = QUEUED_ACTION_HANDLERS[action.type];

  if (!handler) {
    throw new StructuralError(`Unsupported queued action: ${action.type}`);
  }

  yield call(handler, action);
}

const buildRetryEntry = (
  action: QueueableAction,
  retriable: boolean
): AppendRetryEntry | undefined => {
  if (!retriable) return undefined;
  if (isAppendAction(action)) {
    return {
      kind: 'append',
      payload: action.payload as EnqueueAppendAnnotationPayload,
    };
  }
  return {
    kind: 'remove',
    payload: action.payload as EnqueueRemoveAnnotationPayload,
  };
};

/**
 * A failure is retriable unless the error explicitly declares `retriable === false`
 * (SchemaValidationError, StructuralError) — those deterministic failures fail
 * identically on retry, so surfacing a Retry banner would loop forever. Transient
 * backend / network errors carry no such flag and stay retriable (D7-6906).
 */
export const isRetriableError = (error: unknown): boolean =>
  (error as { retriable?: unknown })?.retriable !== false;

function* handleWorkerFailure(action: QueueableAction, error: unknown) {
  console.error('measurementActionWorker failed', error);
  const retriable = isRetriableError(error);
  yield dispatch(
    actions.appendAnnotationFailure({
      measurementId: action.payload.measurementId,
      error: error instanceof Error ? error.message : String(error),
      retriable,
      retryEntry: buildRetryEntry(action, retriable),
    })
  );

  // D7-6906: surface the retriable failure to the scoring form (retry banner).
  const {
    task,
    subTask,
    visit,
    subTaskVisit,
    inventory,
  }: ReturnType<typeof selectStoreDataRelatedToMeasurements> = yield select(
    selectStoreDataRelatedToMeasurements
  );
  const scoringCode =
    subTaskVisit?.scoringCode ||
    visit?.scoringCode ||
    findScoringCode(subTask?.visits || task?.visits || []);
  const failed: Measurement | undefined = yield select(
    (state: { measurements: MeasurementsState }) =>
      state.measurements.data[action.payload.measurementId]
  );
  const failedCode =
    inventory?.measurements?.find(m => m.id === failed?.measurementId)?.code ?? '';
  // Guard like the success-path calls: scoringFormMeasurementsChangeAction can
  // throw synchronously (form listener). If it escaped here it would propagate
  // out of the worker's catch and kill the per-measurement worker loop, leaving
  // its actionChannel without a consumer (D7-6906).
  if (scoringCode && failedCode) {
    try {
      yield call(scoringFormMeasurementsChangeAction, scoringCode, 'update', {
        code: failedCode,
        parentMeasurementId: action.payload.measurementId,
        retriable,
        appendPending: 0,
      });
    } catch (e) {
      console.warn('scoringFormMeasurementsChangeAction (failure notify) failed', e);
    }
  }

  yield call(
    showErrorToast,
    error,
    retriable
      ? 'Failed to save measurement — tap Retry to try again'
      : 'Measurement update was rejected by validation'
  );
}

export function* measurementActionWorker(action: QueueableAction) {
  const measurementId = getQueueMeasurementId(action);
  const isAnnotationAction = isAppendAction(action) || isRemoveAction(action);

  try {
    // Only reader-initiated annotation edits need the cross-tab read-only
    // signal. Lifecycle operations and viewer echoes still share this FIFO
    // queue, but they are not UI edit sessions.
    if (isAnnotationAction) {
      publishEditStart(measurementId);
    }

    yield call(runEnqueuedAction, action);
  } catch (error) {
    if (isAnnotationAction) {
      // D7-7639: this worker is forked from the coordinator, itself forked
      // from `measurementsSaga`, so a throw out of the recovery path cancels
      // all three — the teardown `keepWatcherAlive` prevents, reached around
      // it. `handleWorkerFailure` runs unguarded selects and a toast, hence
      // its own net. Kept as `fork`: `spawn` would lose the coordinator's
      // cleanup of its channels and workers on mode exit.
      try {
        yield call(handleWorkerFailure, action, error);
      } catch (reportError) {
        console.error(
          'measurementActionWorker: failure reporting failed',
          reportError
        );
      }
    } else {
      // Non-annotation actions report their own domain failures. Do not route
      // them through appendAnnotationFailure/retry state.
      console.error('queued measurement action failed', error);
    }
  } finally {
    if (isAnnotationAction) {
      // `publishEditEnd` posts to a BroadcastChannel, and a throw from
      // `finally` escapes even when the body succeeded.
      try {
        publishEditEnd(measurementId);
      } catch (releaseError) {
        console.error(
          'measurementActionWorker: edit-lock release failed',
          releaseError
        );
      }
    }
  }
}

/**
 * Watches `retryAppendAnnotation` actions and re-enqueues the buffered payload.
 * Runs in parallel with the measurement action queue; the re-enqueue lands in
 * the same per-measurement actionChannel and is processed in order with any
 * other pending edits to that record.
 */
export function* retryAppendAnnotationWatcher() {
  while (true) {
    const action: PayloadAction<{ measurementId: string }> = yield take(
      actions.retryAppendAnnotation.type
    );
    const { measurementId } = action.payload;
    const entry: AppendRetryEntry | undefined = yield select(
      (state: { measurements: MeasurementsState }) =>
        state.measurements.appendRetryBufferByMeasurement[measurementId]
    );
    if (!entry) continue;
    if (entry.kind === 'append') {
      yield dispatch(actions.enqueueAppendAnnotation(entry.payload));
    } else {
      yield dispatch(actions.enqueueRemoveAnnotation(entry.payload));
    }
  }
}

/**
 * A new append carries a form-supplied entry as a hint; bump the index until we
 * find one not already used by another annotation on the same instrument.
 */
function findFreeEntry(
  schemaAnnotations: SchemaAnnotation[],
  entry: EntryKey
): EntryKey {
  const usedIndices = new Set(
    schemaAnnotations
      .filter(a => a.instrument === entry.instrument)
      .map(a => a.index)
  );
  let finalEntry = entry;
  while (usedIndices.has(finalEntry.index)) {
    finalEntry = { ...finalEntry, index: finalEntry.index + 1 };
  }
  return finalEntry;
}

/**
 * Promote a legacy singular annotation to annotations[0] so the record becomes
 * fully multi-shape before a new entry is pushed. The singular field is kept;
 * the normalizer dedupes by UID. No-op when the record is already multi-shape,
 * has no singular, or the parent instrument code is unknown. Mutates both
 * arrays in place (they are reassigned onto the draft by the caller).
 */
function promoteLegacySingularAnnotation(
  annotations: AnnotationInGroup[],
  schemaAnnotations: SchemaAnnotation[],
  legacySingular: Partial<AnnotationInstanceObjectType> | undefined,
  parentInstrumentCode: string | undefined,
  schema: ReturnType<typeof getSchema> | undefined,
  context: ExtractValuesContext | undefined
): void {
  if (!legacySingular || annotations.length !== 0 || !parentInstrumentCode) {
    return;
  }
  annotations.push({
    ...legacySingular,
    _link: { instrument: parentInstrumentCode, index: 1 },
  });
  const legacyToolName = legacySingular.metadata?.toolName ?? '';
  const legacyCachedStats =
    (legacySingular.data as { cachedStats?: Record<string, unknown> })
      ?.cachedStats ?? {};
  const legacyValues = schema
    ? schema.extractValues(legacyToolName, legacyCachedStats, context)
    : {};
  schemaAnnotations.push({
    instrument: parentInstrumentCode,
    index: 1,
    ...legacyValues,
  });
}

function buildMultiAnnotationMeasurementData({
  existing,
  rootAnnotations,
  data,
  isMeasurable,
}: {
  existing: Measurement['measurementData'] | undefined;
  rootAnnotations: SchemaAnnotation[] | undefined;
  data: MeasurementDataPayload;
  isMeasurable?: boolean;
}): Measurement['measurementData'] {
  const root = existing ?? ({} as Measurement['measurementData']);

  return {
    ...root,
    isMeasurable: resolveIsMeasurable(root.isMeasurable, isMeasurable),
    annotations: rootAnnotations ?? root.annotations ?? [],
    data,
  };
}

function* resolveWritableMeasurement(measurementId: string) {
  const existing: Measurement | undefined = yield select(
    (state: { measurements: MeasurementsState }) =>
      state.measurements.data[measurementId]
  );
  if (!existing) {
    throw new StructuralError(`Measurement ${measurementId} not found in store`);
  }

  if (existing.locked) {
    yield dispatch(actions.appendAnnotationSuccess(existing));
    return;
  }

  return existing;
}

export function* executeAppend(payload: EnqueueAppendAnnotationPayload) {
  const { measurementId, entry, annotation, measurement } = payload;

  const existing: Measurement | undefined = yield* resolveWritableMeasurement(measurementId);
  if (!existing) {
    return;
  }

  const {
    task,
    subTask,
    visit,
    subTaskVisit,
    inventory,
    activeMeasurement,
  }: ReturnType<typeof selectStoreDataRelatedToMeasurements> = yield select(
    selectStoreDataRelatedToMeasurements
  );
  const scoringCode =
    subTaskVisit?.scoringCode ||
    visit?.scoringCode ||
    findScoringCode(subTask?.visits || task?.visits || []);
  const schema = scoringCode ? getSchema(scoringCode) : undefined;

  const toolName = annotation?.metadata?.toolName ?? '';
  const cachedStats =
    (annotation?.data as { cachedStats?: Record<string, unknown> })
      ?.cachedStats ?? {};
  // Forward the parent record's `data` as opaque context — a schema reads
  // whatever record-level flags it declares; the engine sets/knows none.
  const context = existing.measurementData?.data as
    | ExtractValuesContext
    | undefined;
  const measurementValues = schema
    ? schema.extractValues(toolName, cachedStats, context)
    : {};

  // Look up the instrument code once, for the mixed-shape migration below.
  const inventoryInstrument = inventory?.instruments?.find(
    i => i.id === existing.instrumentId
  );
  const parentInstrumentCode = inventoryInstrument?.code;

  // The persisted Measurement record stores the inventory label id under
  // `measurementId` (the API request param is called `measurementLabelId`).
  const measurementLabelConfig = inventory?.measurements?.find(
    m => m.id === existing.measurementId
  );
  const code = measurementLabelConfig?.code ?? '';

  let capturedFinalEntry: typeof entry = entry;
  // Set when a NEW append's entry index had to be bumped off the form's proposed
  // value because it was already taken. The viewer labelled the live annotation
  // with the proposed (now-wrong) index at draw time, so we relabel it to the
  // index we actually persisted — keeping viewer label == form label (D7-6906).
  let relabelToIndex: number | undefined;

  const updated = produce(existing, draft => {
    const isj = draft.instrumentStateJson ?? {};
    const annotations: AnnotationInGroup[] = isj.annotations ?? [];
    const data = (draft.measurementData?.data ?? {}) as MeasurementDataPayload;
    const schemaAnnotations: SchemaAnnotation[] = data.annotations ?? [];

    promoteLegacySingularAnnotation(
      annotations,
      schemaAnnotations,
      isj.annotation,
      parentInstrumentCode,
      schema,
      context
    );

    // A geometry edit (resize) of an already-appended annotation is routed
    // here too: the MEASUREMENT_MODIFIED "sub-entry" handler reuses the
    // appendAnnotation shape. Upsert by annotationUID — if this annotation is
    // already in the record, update it in place (keep its entry, refresh
    // values) instead of bumping to a free index and pushing a duplicate entry.
    const incomingUID = annotation?.annotationUID;
    const existingIdx =
      incomingUID != null
        ? annotations.findIndex(a => a.annotationUID === incomingUID)
        : -1;

    if (existingIdx >= 0) {
      // Plain snapshot — `annotations[existingIdx]._link` is an immer draft
      // proxy that is revoked once produce() returns. `capturedFinalEntry`
      // escapes this producer (it is handed to scoringFormMeasurementsChangeAction
      // below, which the scoring form reads asynchronously), so it MUST be a
      // plain object. Passing the draft reference throws "Cannot perform 'get'
      // on a proxy that has been revoked" when the form accesses .instrument/.index.
      const existingEntry = { ...annotations[existingIdx]._link };
      capturedFinalEntry = existingEntry;
      annotations[existingIdx] = { ...annotation, _link: existingEntry };

      const schemaIdx = schemaAnnotations.findIndex(
        a => a.instrument === existingEntry.instrument && a.index === existingEntry.index
      );
      if (schemaIdx >= 0) {
        // Preserve other per-annotation fields (e.g. screenshotRef); refresh values.
        schemaAnnotations[schemaIdx] = {
          ...schemaAnnotations[schemaIdx],
          ...measurementValues,
        };
      } else {
        schemaAnnotations.push({
          instrument: existingEntry.instrument,
          index: existingEntry.index,
          ...measurementValues,
        });
      }
    } else {
      // New append: form-supplied entry is a hint; bump until we find a free index.
      const finalEntry = findFreeEntry(schemaAnnotations, entry);
      capturedFinalEntry = finalEntry;
      if (finalEntry.index !== entry.index) {
        relabelToIndex = finalEntry.index;
      }

      annotations.push({
        ...annotation,
        _link: finalEntry,
      });

      schemaAnnotations.push({
        instrument: finalEntry.instrument,
        index: finalEntry.index,
        ...measurementValues,
      });
    }
    /**
     * Track the LATEST drawn/resized split entry as the record's "current
     * slice" (D7-7374).
     *
     * Plain terms
     * -----------
     * A split lesion's Lock button re-selects the lesion right before taking
     * its screenshot, so the viewer returns to "the measurement's slice" first.
     * For a single-annotation lesion that is unambiguous. For a split lesion —
     * several sub-lesions on one record — it must mean the slice the reader
     * was just working on, not wherever the very first sub-lesion happened to
     * be drawn.
     *
     * Technical
     * ---------
     * `instrumentStateJson.measurementState` is the singular field the
     * pre-lock `activate` navigates by (`activateToolWorker` →
     * `activateSeriesWithMeasurement`), and it is normally refreshed on every
     * single-annotation create/update (`prepareRequestData`). The multi-
     * annotation append/resize path never went through that function, so it
     * never touched `measurementState` — leaving it pinned to the FIRST
     * sub-lesion drawn, silently ignored by every later append. Refresh it
     * here the same way, from the viewer-echoed `measurement` for the entry
     * this call just wrote, so it always reflects the most recently
     * drawn/resized entry.
     *
     * `toolIndex` is stamped explicitly (not taken from the raw echo) because
     * it is NOT part of OHIF's standard measurement schema — `prepareRequestData`
     * only ever has one because it defaults it itself
     * (`measurement.toolIndex = index`). `activateToolWorker`'s `isBound` check
     * requires `measurementState.toolIndex === config.index`; leaving it
     * `undefined` here would make `isBound` false and skip navigation
     * entirely — a worse regression than the original bug. `existing.index`
     * is this record's own top-level index, the exact value `config.index`
     * is compared against.
     */
    if (measurement) {
      draft.instrumentStateJson = {
        ...isj,
        annotations,
        measurementState: { ...measurement, toolIndex: existing.index },
      };
    } else {
      draft.instrumentStateJson = { ...isj, annotations };
    }

    let nextData: MeasurementDataPayload = {
      ...data,
      annotations: schemaAnnotations,
    };
    if (schema) {
      const aggregates = schema.computeAggregates(schemaAnnotations);
      nextData = { ...nextData, ...aggregates };
    }
    // Only a registered schema values the entries. Without one they are bare
    // `{ instrument, index }` pairs the BE rejects, so the form keeps the root array.
    draft.measurementData = buildMultiAnnotationMeasurementData({
      existing: draft.measurementData,
      rootAnnotations: schema ? schemaAnnotations : undefined,
      data: nextData,
      isMeasurable: activeMeasurement?.isMeasurable,
    });

    if (schema) {
      draft.instrumentStateText = schema.computeStateText(draft);
    }
  });

  if (schema) {
    const validation = schema.validate(updated);
    if (!validation.ok) {
      throw new SchemaValidationError(
        `Schema validation failed: ${validation.errors.join(', ')}`
      );
    }
  }

  const clinicalTrialId = task?.clinicalTrialId ?? '';
  const taskId = subTask?.id || task?.id || '';
  if (!clinicalTrialId || !taskId) {
    throw new StructuralError('Missing clinicalTrialId / taskId for append');
  }

  const requestData: MeasurementReqParams = {
    index: updated.index,
    instrumentId: updated.instrumentId,
    instrumentStateJson: updated.instrumentStateJson,
    instrumentStateText: updated.instrumentStateText,
    measurementLabelId: existing.measurementId,
    measurementName: updated.measurementName,
    measurementData: updated.measurementData,
    /**
     * Re-send the record's `permanentData` unchanged on this multi-annotation PUT.
     *
     * Plain terms
     * -----------
     * A lesion carried over from an earlier visit ("permanent") keeps fixed
     * details — its location and type. When the reader adds or removes one of its
     * split sub-lesions, we must send those details back untouched: the server
     * refuses to save a permanent lesion whose details are missing.
     *
     * Technical
     * ---------
     * BE validates a permanent measurement's `permanentData` against
     * `permanentData.schema.json` and rejects a null/absent value (400
     * "Permanent data does not match validation schema … must be of object type,
     * but actual type is null") — the RANO Follow-up "Split into multiple lesions"
     * append/remove round-trip failed on exactly this (D7-7283). This PUT path
     * (D7-6906) originally omitted the field entirely. Forward `existing.permanentData`
     * verbatim; it is immutable, so re-sending the stored value can never conflict.
     * Presence-conditional (not unconditional) mirrors `prepareRequestData` — a
     * non-permanent record has no `permanentData` and keeps omitting it, so the
     * RECIST-style multi-annotation appends that work today are unchanged.
     */
    ...(existing.permanentData ? { permanentData: existing.permanentData } : {}),
  };

  const response: Resp<typeof api.updateMeasurement> = yield call(
    api.updateMeasurement,
    measurementId,
    clinicalTrialId,
    taskId,
    requestData
  );

  yield dispatch(
    actions.appendAnnotationSuccess(response.data)
  );

  // Keep the live viewer label in sync with the entry we actually persisted.
  // Only needed when the draw-time index was bumped (D7-6906). Explicit RPC
  // method; fire-and-forget — a relabel failure must not fail the (already
  // persisted) append.
  if (relabelToIndex !== undefined && annotation?.annotationUID) {
    try {
      yield call(viewerRpc.requests.measurement.relabelAnnotation, {
        annotationUID: annotation.annotationUID,
        index: relabelToIndex,
      });
    } catch (e) {
      console.warn('relabel annotation request failed', e);
    }
  }

  // Fire-and-forget form notification. The append is already persisted and
  // appendAnnotationSuccess dispatched; this call can throw synchronously
  // (emitToolChanges → store.dispatch, getPeer().emit(), window.dispatchEvent →
  // a Preact form listener). If it escaped, the worker catch would misclassify an
  // already-committed append as a retriable failure (double-decrement of the
  // pending counter + a phantom retry buffer → a Retry tap duplicates the
  // annotation). Swallow it like the relabel guard above (D7-6906).
  if (code && scoringCode) {
    try {
      yield call(scoringFormMeasurementsChangeAction, scoringCode, 'update', {
        code,
        value: null,
        parentMeasurementId: measurementId,
        appendAnnotation: {
          entry: capturedFinalEntry,
          annotation,
          measurementValues,
        },
      });
    } catch (e) {
      console.warn('scoringFormMeasurementsChangeAction (append) failed', e);
    }
  }
}

export function* executeRemove(payload: EnqueueRemoveAnnotationPayload) {
  const { measurementId, annotationUID } = payload;

  const existing: Measurement | undefined = yield* resolveWritableMeasurement(measurementId);
  if (!existing) {
    return;
  }

  const {
    task,
    subTask,
    visit,
    subTaskVisit,
    inventory,
  }: ReturnType<typeof selectStoreDataRelatedToMeasurements> = yield select(
    selectStoreDataRelatedToMeasurements
  );
  const scoringCode =
    subTaskVisit?.scoringCode ||
    visit?.scoringCode ||
    findScoringCode(subTask?.visits || task?.visits || []);
  const schema = scoringCode ? getSchema(scoringCode) : undefined;

  const measurementLabelConfig = inventory?.measurements?.find(
    m => m.id === existing.measurementId
  );
  const code = measurementLabelConfig?.code ?? '';

  const target = (existing.instrumentStateJson?.annotations ?? []).find(
    a => a.annotationUID === annotationUID
  );
  if (!target) {
    // Nothing to do; clear the pending flag and bail.
    yield dispatch(
      actions.appendAnnotationSuccess(existing)
    );
    return;
  }
  const targetEntry = target._link;

  const updated = produce(existing, draft => {
    const isj = draft.instrumentStateJson ?? {};
    const annotations = (isj.annotations ?? []).filter(
      a => a.annotationUID !== annotationUID
    );
    const nextIsj = { ...isj, annotations };
    // Mixed-shape cleanup: if the removed annotation came from the legacy
    // singular (which executeAppend promotes into annotations[] on first
    // append but keeps in `instrumentStateJson.annotation`), null the
    // singular too. Otherwise getAnnotations re-promotes it as a phantom
    // entry on the next read and the deleted entry reappears.
    if (nextIsj.annotation?.annotationUID === annotationUID) {
      delete nextIsj.annotation;
    }
    draft.instrumentStateJson = nextIsj;

    const data = (draft.measurementData?.data ?? {}) as MeasurementDataPayload;
    // NOTE: filtering the SchemaAnnotation here also drops its per-annotation
    // `screenshotRef` (it lives inside the entry), so deleting an annotation
    // removes its screenshot reference atomically with the canvas object that
    // removeAnnotationWorker (viewer side) already removes. Physically deleting
    // the stored screenshot blob would need a backend delete endpoint (none
    // today); screenshotIds[] is backend-managed and not sent on update.
    const schemaAnnotations = (data.annotations ?? []).filter(
      a =>
        !(a.instrument === targetEntry.instrument && a.index === targetEntry.index)
    );
    let nextData: MeasurementDataPayload = {
      ...data,
      annotations: schemaAnnotations,
    };
    if (schema) {
      const aggregates = schema.computeAggregates(schemaAnnotations);
      nextData = { ...nextData, ...aggregates };
    }
    draft.measurementData = buildMultiAnnotationMeasurementData({
      existing: draft.measurementData,
      rootAnnotations: schema ? schemaAnnotations : undefined,
      data: nextData,
    });

    if (schema) {
      draft.instrumentStateText = schema.computeStateText(draft);
    }
  });

  if (schema) {
    const validation = schema.validate(updated);
    if (!validation.ok) {
      throw new SchemaValidationError(
        `Schema validation failed: ${validation.errors.join(', ')}`
      );
    }
  }

  const clinicalTrialId = task?.clinicalTrialId ?? '';
  const taskId = subTask?.id || task?.id || '';
  if (!clinicalTrialId || !taskId) {
    throw new StructuralError('Missing clinicalTrialId / taskId for remove');
  }

  const requestData: MeasurementReqParams = {
    index: updated.index,
    instrumentId: updated.instrumentId,
    instrumentStateJson: updated.instrumentStateJson,
    instrumentStateText: updated.instrumentStateText,
    measurementLabelId: existing.measurementId,
    measurementName: updated.measurementName,
    measurementData: updated.measurementData,
    /**
     * Re-send the record's `permanentData` unchanged on this multi-annotation PUT.
     *
     * Plain terms
     * -----------
     * A lesion carried over from an earlier visit ("permanent") keeps fixed
     * details — its location and type. When the reader adds or removes one of its
     * split sub-lesions, we must send those details back untouched: the server
     * refuses to save a permanent lesion whose details are missing.
     *
     * Technical
     * ---------
     * BE validates a permanent measurement's `permanentData` against
     * `permanentData.schema.json` and rejects a null/absent value (400
     * "Permanent data does not match validation schema … must be of object type,
     * but actual type is null") — the RANO Follow-up "Split into multiple lesions"
     * append/remove round-trip failed on exactly this (D7-7283). This PUT path
     * (D7-6906) originally omitted the field entirely. Forward `existing.permanentData`
     * verbatim; it is immutable, so re-sending the stored value can never conflict.
     * Presence-conditional (not unconditional) mirrors `prepareRequestData` — a
     * non-permanent record has no `permanentData` and keeps omitting it, so the
     * RECIST-style multi-annotation appends that work today are unchanged.
     */
    ...(existing.permanentData ? { permanentData: existing.permanentData } : {}),
  };

  const response: Resp<typeof api.updateMeasurement> = yield call(
    api.updateMeasurement,
    measurementId,
    clinicalTrialId,
    taskId,
    requestData
  );

  yield dispatch(
    actions.appendAnnotationSuccess(response.data)
  );

  // Fire-and-forget form notification — see the executeAppend guard above. The
  // remove is already persisted; a synchronous throw here must not be reclassified
  // as a retriable failure (double-decrement + phantom retry → duplicate) (D7-6906).
  if (code && scoringCode) {
    try {
      yield call(scoringFormMeasurementsChangeAction, scoringCode, 'update', {
        code,
        value: null,
        parentMeasurementId: measurementId,
        removeAnnotation: { annotationUID },
      });
    } catch (e) {
      console.warn('scoringFormMeasurementsChangeAction (remove) failed', e);
    }
  }
}

/**
 * Clean up a PERMANENT measurement instead of deleting it (D7-7312).
 *
 * Plain terms
 * -----------
 * A lesion copied over from baseline ("permanent") exists in every follow-up
 * visit, and the server keeps its record forever — it refuses to delete it. So
 * when the reader removes such a lesion's drawn measurement on a follow-up, we
 * must NOT try to delete the record. Instead we clear its drawn shape and update
 * the record so it keeps existing (ready to be re-measured), with no measurement
 * attached.
 *
 * Technical
 * ---------
 * BE rejects DELETE on a permanent measurement (`business:measurement_processing`
 * — "Measurement can't be processed since it is permanent") but ACCEPTS a PUT
 * update. This empties the annotations (both the `annotations[]` array and the
 * legacy singular `annotation`), recomputes the schema aggregates / state text
 * over the empty set, and PUTs the reduced record via `api.updateMeasurement` —
 * never DELETE. Mirrors `executeRemove` but clears the whole set rather than one
 * entry (a primary annotation lives in the singular slot, which `executeRemove`'s
 * `annotations[]`-only target lookup would miss).
 */
export function* executePermanentCleanup(measurementId: string) {
  const existing: Measurement | undefined = yield select(
    (state: { measurements: MeasurementsState }) => state.measurements.data[measurementId]
  );
  if (!existing) {
    throw new StructuralError(`Measurement ${measurementId} not found in store`);
  }

  const {
    task,
    subTask,
    visit,
    subTaskVisit,
  }: ReturnType<typeof selectStoreDataRelatedToMeasurements> = yield select(
    selectStoreDataRelatedToMeasurements
  );

  const scoringCode =
    subTaskVisit?.scoringCode ||
    visit?.scoringCode ||
    findScoringCode(subTask?.visits || task?.visits || []);
  const schema = scoringCode ? getSchema(scoringCode) : undefined;

  const updated = produce(existing, draft => {
    const isj = draft.instrumentStateJson ?? {};
    const nextIsj = { ...isj, annotations: [] };
    // Drop the legacy singular too, else getAnnotations re-promotes it as a
    // phantom entry on the next read and the cleared shape reappears.
    delete nextIsj.annotation;
    // The scoring form re-reads lad/sad from `measurementState.data` on revive
    // (RANO `viewerMeasurementFill`), so a cleared permanent lesion must strip it
    // too — otherwise the numbers reappear in the form after reload even though
    // the drawing is gone (D7-5720).
    if (nextIsj.measurementState) {
      nextIsj.measurementState = {
        ...(nextIsj.measurementState as Record<string, unknown>),
        data: {},
      } as typeof nextIsj.measurementState;
    }
    draft.instrumentStateJson = nextIsj;

    const data = (draft.measurementData?.data ?? {}) as MeasurementDataPayload;
    let nextData: MeasurementDataPayload = { ...data, annotations: [] };
    if (schema) {
      const aggregates = schema.computeAggregates([]);
      nextData = { ...nextData, ...aggregates };
    }
    // RANO carries the schema values at the TOP level of `measurementData`
    // (`annotations` / `ppd`), not only under `.data`, so clear those too — the
    // previous code emptied only `.data.annotations`, which no consumer reads
    // (D7-5720).
    draft.measurementData = buildMultiAnnotationMeasurementData({
      existing: draft.measurementData,
      rootAnnotations: [],
      data: nextData,
    });

    if (schema) {
      draft.instrumentStateText = schema.computeStateText(draft);
    }
  });

  if (schema) {
    const validation = schema.validate(updated);
    if (!validation.ok) {
      throw new SchemaValidationError(
        `Schema validation failed: ${validation.errors.join(', ')}`
      );
    }
  }

  const clinicalTrialId = task?.clinicalTrialId ?? '';
  const taskId = subTask?.id || task?.id || '';
  if (!clinicalTrialId || !taskId) {
    throw new StructuralError('Missing clinicalTrialId / taskId for permanent cleanup');
  }

  const requestData: MeasurementReqParams = {
    index: updated.index,
    instrumentId: updated.instrumentId,
    instrumentStateJson: updated.instrumentStateJson,
    instrumentStateText: updated.instrumentStateText,
    measurementLabelId: existing.measurementId,
    measurementName: updated.measurementName,
    measurementData: updated.measurementData,
    /**
     * Re-send the baseline `permanentData` unchanged.
     *
     * Plain terms
     * -----------
     * The lesion's fixed details (location, type) came from baseline and never
     * change on a follow-up. Clearing the drawn shape must not drop them — the
     * server refuses to save a permanent lesion whose details are missing.
     *
     * Technical
     * ---------
     * BE validates a permanent measurement's `permanentData` against
     * `permanentData.schema.json` and rejects null/absent ("Permanent data does
     * not match validation schema … must be of object type, but actual type is
     * null"). A permanent record always carries it, so forward
     * `existing.permanentData` verbatim; cleanup only touches the drawn
     * `measurementData` / annotations.
     */
    permanentData: existing.permanentData,
  };

  const response: Resp<typeof api.updateMeasurement> = yield call(
    api.updateMeasurement,
    measurementId,
    clinicalTrialId,
    taskId,
    requestData
  );

  // Sync the store with the emptied record. Without this, `state.measurements`
  // keeps the pre-cleanup measurement (singular `annotation` + `annotations[]`
  // intact), and `reviveMeasurementsWorker`'s `onlyWithAnnotations` filter would
  // re-push the cleared annotation into the viewer on the next revival — the
  // cleared shape would reappear. `updateMeasurementSuccess` writes the reduced
  // record back (not `appendAnnotationSuccess`, whose `decAppendPending` would
  // drive the never-incremented append counter negative on this direct-PUT path).
  yield dispatch(actions.updateMeasurementSuccess(response.data));
}

// A queue channel is strictly serial, so a worker that never returns blocks every
// later event for that annotation — for the rest of the session, with nothing
// surfaced. `takeLatest` used to cancel a stalled worker on the next event; the
// queue has no such backstop and `RESTClient` sets no request timeout, so the
// bound lives here. Cancelling mid-PUT can leave a write that landed without the
// form hearing it, which is the safe direction: the opposite is D7-8035.
export const STORE_MEASUREMENT_TIMEOUT_MS = 30000;

export const withWriteTimeout =
  <A extends { type?: string }>(worker: WatcherWorker<A>): WatcherWorker<A> =>
    function* boundedWorker(action: A) {
      const outcome = (yield race({
        done: call(worker, action),
        timedOut: delay(STORE_MEASUREMENT_TIMEOUT_MS),
      })) as { timedOut?: true };

      if (outcome.timedOut) {
        console.error(
          `measurementsSaga: "${action?.type ?? 'unknown'}" exceeded ${STORE_MEASUREMENT_TIMEOUT_MS}ms; cancelled so the queue can drain`
        );
      }
    };

// See D7-7639 / D7-7995: shared with `segmentation.saga.ts` so the two sagas
// can't drift out of sync on this safety net again.
const keepWatcherAlive = createKeepWatcherAlive('measurementsSaga');

export function* measurementsSaga() {
  yield fork(watchViewerReady);
  yield fork(
    takeQueue,
    QUEUEABLE_ACTION_TYPES,
    measurementActionWorker,
    { getChannelId: getQueueMeasurementId }
  );
  yield fork(retryAppendAnnotationWatcher);

  yield takeLatest(
    actions.activateMeasurement.type,
    keepWatcherAlive(activateMeasurementsWorker)
  );
  yield takeLatest(
    actions.deactivateMeasurement.type,
    keepWatcherAlive(deactivateMeasurementsWorker)
  );
  yield takeLatest(
    actions.showMeasurement.type,
    keepWatcherAlive(showHideMeasurementsWorker(true), 'Failed to show lesion')
  );
  yield takeLatest(
    actions.hideMeasurement.type,
    keepWatcherAlive(showHideMeasurementsWorker(false), 'Failed to hide lesion')
  );
  // Lock, unlock, form delete/status update, and screenshotNA are consumed by
  // the per-measurement FIFO coordinator above (D7-7658, D7-7682). Registering
  // independent watchers here would run the same action twice and reintroduce
  // the race.

  // `createMeasurement` / `updateMeasurement` have no watcher: every caller
  // needs the write's outcome and so goes through `yield call(worker, action)`.
  yield takeLatest(
    actions.deleteMeasurement.type,
    keepWatcherAlive(deleteMeasurementsWorker)
  );
  yield takeLatest(
    actions.screenshotMeasurement.type,
    keepWatcherAlive(
      sendScreenshotMeasurementsWorkerToViewer,
      'Failed to request the screenshot'
    )
  );

  // One FIFO per annotation: a global `takeLatest` let a second viewer event cancel
  // another measurement's worker mid-write, so the row saved but the form never heard.
  yield fork(
    takeQueue,
    [Rpc.Events.Viewer.STORE_MEASUREMENT],
    withWriteTimeout(
      keepWatcherAlive(storeMeasurementWorker, 'Failed to save the measurement')
    ),
    { getChannelId: getStoreMeasurementChannelId }
  );

  yield takeLatest(
    Rpc.Events.Viewer.SERIES_CHANGED,
    keepWatcherAlive(seriesChangedWorker, 'Failed to apply the series change')
  );
  yield takeLatest(
    Rpc.Events.Viewer.READY,
    keepWatcherAlive(assembleMeasurements, 'Failed to load lesions')
  );
}
